import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CustomDateRange from "./CustomDateRange";
import { capitalizeFirstLetter } from "../../../components/reusableComponent/CapitalLetter";
import { useGetCampaignLeadAnalysisDataQuery, useGetCampaignTasksDataQuery, useGetCampaignTeamPerformanceDataQuery, useGetFilterAssigneeByCampaignIdQuery, useGetFilterBundleQuery, useGetLeadClickToExpandQuery, useGetWhatsappVolumeQuery } from '../../../Services/dashboard/dashboard';
import axios from 'axios';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    BarChart,
    Bar,
    Cell,
    PieChart,
    Pie,
    Legend,
} from 'recharts';
import ChartWidget from './ChartWidget';
import FullscreenModal from './FullscreenModal';
import TableModal from '../../../components/reusableComponent/TableModal';
import LeadsPerCampaignChart from './LeadsPerCampaignChart';
import OpenLeadsPerCampaignChart from './OpenLeadsPerCampaignChart';
import ConversionFunnel from './ConversionFunnel';
import AssigneePerformanceChart from './AssigneePerformanceChart';
import ExplodingPieChart from './ExplodingPieChart';
import { useNavigate } from 'react-router-dom';
import { useGetBroadcastTemplatesQuery } from '../../../Services/campign/campignList';
import { USER_TEAM_ID } from '../../../constants';
import "../DashboardPageLayout.scss";

const ResponsiveContainerC = ResponsiveContainer as any;
const ComposedChartC = ComposedChart as any;
const CartesianGridC = CartesianGrid as any;
const XAxisC = XAxis as any;
const YAxisC = YAxis as any;
const TooltipC = Tooltip as any;
const LineC = Line as any;
const AreaC = Area as any;
const BarChartC = BarChart as any;
const BarC = Bar as any;
const CellC = Cell as any;
const PieChartC = PieChart as any;
const PieC = Pie as any;
const LegendC = Legend as any;

const LEAD_CHANNEL_COLORS = ["#f97316", "#8b5cf6", "#ef4444", "#64748b", "#6366f1", "#ec4899", "#475569"];
// Lead funnel: reference-style palette (dark purple-blue, gray-blue, magenta, teal) + extended distinct colors, no repeat for large data
const CONVERSION_FUNNEL_COLORS = [
    "#4a45a3", "#6a7796", "#7b8ab8", "#c751c7", "#28c0a8", // reference: Work In Progress, Reopen, closed, closed2
    "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#84cc16", "#22c55e",
    "#14b8a6", "#06b6d4", "#6366f1", "#6366f1", "#64748b", "#475569", "#78716c", "#a16207", "#15803d", "#0f766e",
];
/** Get a distinct color for funnel stage at index; no repeat – uses palette then generated HSL for extra stages. */
function getFunnelColor(index: number): string {
    if (index < CONVERSION_FUNNEL_COLORS.length) return CONVERSION_FUNNEL_COLORS[index];
    const hue = (index * 37) % 360;
    return `hsl(${hue}, 55%, 48%)`;
}
const CAMPAIGN_PERFORMANCE_COLORS = ['#6366f1', '#10b981', '#ec4899', '#8b5cf6', '#f59e0b'];

/** Same as EmailPieChart: Active, Closed, WIP, Reopen – used for Conversion engagement pie colors and labels. */
const CONVERSION_ENGAGEMENT_CONFIG = [
    { key: "active", label: "Active", color: "#009DFF" },
    { key: "closed", label: "Closed", color: "#198754" },
    { key: "wip", label: "WIP", color: "#ffc107" },
    { key: "reopen", label: "Reopen", color: "#90EE90" },
];

/** Max chars per line; same behaviour as Leads per campaign chart. */
const LEAD_TRENDS_LABEL_MAX_CHARS = 14;
const LEAD_TRENDS_LABEL_FONT_SIZE = 11;

/** Split into 1, 2, or 3 lines at word boundaries. maxLines 3 when many campaigns. */
function splitCampaignLabel(text: string, maxChars: number = LEAD_TRENDS_LABEL_MAX_CHARS, maxLines: number = 2): string[] {
    const t = (text || '').trim();
    if (!t) return [''];
    if (t.length <= maxChars) return [t];
    const words = t.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return [t];
    if (maxLines >= 3 && words.length >= 3) {
        const n1 = Math.ceil(words.length / 3);
        const n2 = Math.ceil((2 * words.length) / 3);
        return [
            words.slice(0, n1).join(' '),
            words.slice(n1, n2).join(' '),
            words.slice(n2).join(' '),
        ];
    }
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

/** Responsive gap (px) between chart plot and X-axis labels: desktop comfortable, tablet medium, mobile readable. */
function getLeadTrendsLabelGap(): number {
    if (typeof window === 'undefined') return 10;
    const w = window.innerWidth;
    if (w >= 1024) return 10;
    if (w >= 768) return 8;
    return 6;
}

/** Custom X-axis tick for Lead Trends: same as Leads per campaign – fixed font, 1–3 lines, underscores as spaces. */
const LeadTrendsXAxisTick = (props: any) => {
    const { x, y, payload, dataLength } = props;
    const n = dataLength ?? 0;
    const [labelGap, setLabelGap] = useState(() => getLeadTrendsLabelGap());
    useEffect(() => {
        const onResize = () => setLabelGap(getLeadTrendsLabelGap());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const value = (payload?.value ?? '').trim().replace(/_/g, ' ');
    const maxChars = n <= 3 ? 999 : n >= 6 ? 10 : n >= 4 ? 12 : LEAD_TRENDS_LABEL_MAX_CHARS;
    const maxLines = n > 4 ? 3 : 2;
    const lines = splitCampaignLabel(value, maxChars, maxLines);
    const fill = '#94a3b8';
    const fontWeight = 500;
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={labelGap} textAnchor="middle" fill={fill} fontSize={LEAD_TRENDS_LABEL_FONT_SIZE} fontWeight={fontWeight} style={{ fontFamily: 'inherit' }}>
                {lines.length === 1 ? (
                    lines[0]
                ) : lines.length === 2 ? (
                    <>
                        <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
                        <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
                    </>
                ) : (
                    <>
                        <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
                        <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
                        <tspan x={0} dy="1.15em" textAnchor="middle">{lines[2]}</tspan>
                    </>
                )}
            </text>
        </g>
    );
};




/** Fullscreen Conversion engagement: ExplodingPieChart (same as card) or legacy two-pie layout. */
function ConversionEngagementFullscreen({
    conversionExplodingMainData,
    conversionFunnelData,
    conversionFunnelNormalized,
    conversionFunnelTotal,
    campaignId,
    setFunnelLeadsModalStage,
    setFunnelLeadsModalOpen,
}: {
    conversionExplodingMainData: import("./ExplodingPieChart").ExplodingPieDataItem[];
    conversionFunnelData: any[];
    conversionFunnelNormalized: any[];
    conversionFunnelTotal: number;
    campaignId: string;
    setFunnelLeadsModalStage: (v: any) => void;
    setFunnelLeadsModalOpen: (v: boolean) => void;
}) {
    const [selectedSegment, setSelectedSegment] = useState<"wip" | "closed" | null>(null);

    const mainPieData = useMemo(() => {
        const raw = conversionFunnelData || [];
        const hasStatusType = raw.some((d: any) => d.status_type != null && d.status_type !== "");
        if (!hasStatusType || raw.length === 0) return null;
        const getVal = (d: any) => Number(d.value ?? d.leads ?? d.lead_count ?? 0);
        const configByKey: Record<string, { label: string; color: string }> = {};
        CONVERSION_ENGAGEMENT_CONFIG.forEach((c) => { configByKey[c.key] = { label: c.label, color: c.color }; });
        const groups: Record<string, { value: number }> = { active: { value: 0 }, wip: { value: 0 }, closed: { value: 0 }, reopen: { value: 0 } };
        raw.forEach((d: any) => {
            const st = (d.status_type || "").toLowerCase();
            const v = getVal(d);
            if (st === "active") groups.active.value += v;
            else if (st === "wip") groups.wip.value += v;
            else if (st === "closed") groups.closed.value += v;
            else if (st === "reopen") groups.reopen.value += v;
        });
        const wipCount = raw.filter((d: any) => (d.status_type || "").toLowerCase() === "wip").length;
        const closedCount = raw.filter((d: any) => (d.status_type || "").toLowerCase() === "closed").length;
        const minClickable = 0.01, minShow = 0.01;
        const activeConfig = configByKey.active, wipConfig = configByKey.wip, closedConfig = configByKey.closed, reopenConfig = configByKey.reopen;
        return [
            { name: "Active", displayLabel: activeConfig?.label ?? "Active", value: groups.active.value > 0 ? groups.active.value : minShow, status_type: "active", color: activeConfig?.color ?? "#10b981", isAggregate: false },
            { name: "Total WIP", displayLabel: wipConfig?.label ?? "WIP", value: groups.wip.value > 0 ? groups.wip.value : (wipCount > 0 ? minClickable : minShow), status_type: "wip", color: wipConfig?.color ?? "#ef4444", isAggregate: true },
            { name: "Total Closed", displayLabel: closedConfig?.label ?? "Closed", value: groups.closed.value > 0 ? groups.closed.value : (closedCount > 0 ? minClickable : minShow), status_type: "closed", color: closedConfig?.color ?? "#f59e0b", isAggregate: true },
            { name: "Reopen", displayLabel: reopenConfig?.label ?? "Reopen", value: groups.reopen.value > 0 ? groups.reopen.value : minShow, status_type: "reopen", color: reopenConfig?.color ?? "#6366f1", isAggregate: false },
        ];
    }, [conversionFunnelData]);

    const subPieData = useMemo(() => {
        const raw = conversionFunnelData || [];
        if (!selectedSegment || (selectedSegment !== "wip" && selectedSegment !== "closed")) return [];
        const st = selectedSegment;
        const filtered = raw.filter((d: any) => (d.status_type || "").toLowerCase() === st);
        const colorOffset = st === "wip" ? 1 : 5;
        return filtered.map((d: any, index: number) => {
            const stageName = d.funnel_stage ?? d.name ?? d.stage_name ?? d.default_stage ?? "—";
            const displayLabel = typeof stageName === "string" ? stageName.replace(/\b\w/g, (c: string) => c.toUpperCase()) : String(stageName);
            const val = Number(d.lead_count ?? d.value ?? d.leads ?? 0);
            return { name: stageName, displayLabel: d.displayLabel ?? displayLabel, value: val > 0 ? val : 0.01, status_type: d.status_type, color: d.color ?? getFunnelColor(colorOffset + index) };
        });
    }, [conversionFunnelData, selectedSegment]);

    const sourceData = mainPieData != null ? mainPieData : conversionFunnelNormalized;
    const showSubPie = mainPieData != null && subPieData.length > 0;

    if (conversionExplodingMainData.length > 0) {
        return (
            <div style={{ display: "flex", flexDirection: "column", padding: "16px", minHeight: "400px", gap: "16px" }}>
                <ExplodingPieChart
                    mainData={conversionExplodingMainData}
                    height={400}
                    onSubSliceClick={({ category, status_type, value }) => {
                        setFunnelLeadsModalStage({
                            title: `Leads – ${category}`,
                            graph_name: "leads_funnel",
                            value: value ?? category,
                            status_type: status_type ?? "closed",
                            campaign_id: campaignId ?? "",
                        });
                        setFunnelLeadsModalOpen(true);
                    }}
                />
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", padding: "16px", minHeight: "400px", gap: "16px" }}>
            <div style={{ position: "relative", display: "flex", flex: 1, minHeight: "280px", gap: "16px", alignItems: "stretch" }}>
                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: "260px" }}>
                    {sourceData.length === 0 ? (
                        <div style={{ color: "#94a3b8", fontSize: "14px" }}>No funnel data</div>
                    ) : (
                        <>
                            <ResponsiveContainerC width="100%" height={260}>
                                <PieChartC>
                                    <PieC
                                        data={sourceData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="58%"
                                        outerRadius="85%"
                                        paddingAngle={2}
                                        onClick={(_data: any, index: number) => {
                                            const row = sourceData[index];
                                            if (!row?.name) return;
                                            if (mainPieData != null && row.isAggregate) {
                                                setSelectedSegment(row.status_type === "wip" ? "wip" : row.status_type === "closed" ? "closed" : null);
                                                return;
                                            }
                                            setFunnelLeadsModalStage({ title: `Leads – ${row.displayLabel ?? row.name}`, graph_name: "leads_funnel", value: row.name, status_type: row.status_type ?? "closed", campaign_id: campaignId ?? "" });
                                            setFunnelLeadsModalOpen(true);
                                        }}
                                    >
                                        {sourceData.map((entry: any, index: number) => (
                                            <CellC key={`fs-funnel-${index}`} fill={entry.color} style={{ cursor: entry?.name ? "pointer" : "default" }} />
                                        ))}
                                    </PieC>
                                    <TooltipC
                                        content={({ active, payload }: any) => {
                                            if (!active || !payload?.length) return null;
                                            const p = payload[0].payload;
                                            const label = p.displayLabel ?? p.name;
                                            const val = Number(p.value);
                                            const showClickHint = p.isAggregate && mainPieData != null;
                                            const displayLeads = val < 0.1 ? "0" : (val >= 1 ? val.toLocaleString() : String(val));
                                            return (
                                                <div style={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 14px", minWidth: "140px" }}>
                                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: "6px" }}>{label}</div>
                                                    <div style={{ fontSize: "13px", color: "#64748b" }}>Leads: {displayLeads}</div>
                                                    {showClickHint && <div style={{ fontSize: "12px", color: "#6366f1", marginTop: "4px" }}>Click to see sub-stages</div>}
                                                </div>
                                            );
                                        }}
                                    />
                                </PieChartC>
                            </ResponsiveContainerC>
                            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none", zIndex: 2 }}>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", marginBottom: "4px" }}>TOTAL LEADS</div>
                                <div style={{ fontSize: "28px", fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>{conversionFunnelTotal.toLocaleString()}</div>
                            </div>
                        </>
                    )}
                </div>
                {showSubPie && (
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "260px" }}>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>{selectedSegment === "wip" ? "WIP stages" : "Closed stages"}</div>
                        <div style={{ position: "relative", width: "100%", height: 220 }}>
                            <ResponsiveContainerC width="100%" height={220}>
                                <PieChartC>
                                    <PieC
                                        data={subPieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="50%"
                                        outerRadius="80%"
                                        paddingAngle={2}
                                        onClick={(_data: any, index: number) => {
                                            const row = subPieData[index];
                                            if (row?.name) {
                                                setFunnelLeadsModalStage({ title: `Leads – ${row.displayLabel ?? row.name}`, graph_name: "leads_funnel", value: row.name, status_type: row.status_type ?? "closed", campaign_id: campaignId ?? "" });
                                                setFunnelLeadsModalOpen(true);
                                            }
                                        }}
                                    >
                                        {subPieData.map((entry: any, index: number) => (
                                            <CellC key={`fs-sub-${index}`} fill={entry.color} style={{ cursor: "pointer" }} />
                                        ))}
                                    </PieC>
                                    <TooltipC
                                        content={({ active, payload }: any) => {
                                            if (!active || !payload?.length) return null;
                                            const p = payload[0].payload;
                                            const v = Number(p.value);
                                            const leadDisplay = v < 0.1 ? "0" : (v >= 1 ? v.toLocaleString() : String(v));
                                            return (
                                                <div style={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 14px", minWidth: "140px" }}>
                                                    <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: "6px" }}>{p.displayLabel ?? p.name}</div>
                                                    <div style={{ fontSize: "13px", color: "#64748b" }}>Leads: {leadDisplay}</div>
                                                </div>
                                            );
                                        }}
                                    />
                                </PieChartC>
                            </ResponsiveContainerC>
                            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none", zIndex: 2 }}>
                                <div style={{ fontSize: "10px", fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", marginBottom: "2px" }}>TOTAL LEADS</div>
                                <div style={{ fontSize: "22px", fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>{subPieData.reduce((s, d) => s + (d.value >= 0.1 ? Math.round(Number(d.value)) : 0), 0).toLocaleString()}</div>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", justifyContent: "center", marginTop: "8px" }}>
                            {subPieData.map((row: any) => (
                                <div key={row.name} onClick={() => { setFunnelLeadsModalStage({ title: `Leads – ${row.displayLabel ?? row.name}`, graph_name: "leads_funnel", value: row.name, status_type: row.status_type ?? "closed", campaign_id: campaignId ?? "" }); setFunnelLeadsModalOpen(true); }} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "4px 8px", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: 500, color: "#1e293b" }}>
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                                    <span>{row.displayLabel ?? row.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {sourceData.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
                    {sourceData.map((row: any) => (
                        <div
                            key={row.name}
                            onClick={() => {
                                if (row?.isAggregate) setSelectedSegment(row.status_type === "wip" ? "wip" : row.status_type === "closed" ? "closed" : null);
                                else if (row?.name) {
                                    setFunnelLeadsModalStage({ title: `Leads – ${row.displayLabel ?? row.name}`, graph_name: "leads_funnel", value: row.name, status_type: row.status_type ?? "closed", campaign_id: campaignId ?? "" });
                                    setFunnelLeadsModalOpen(true);
                                }
                            }}
                            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px", cursor: row?.name ? "pointer" : "default", background: selectedSegment && row?.status_type === selectedSegment ? "rgba(99, 102, 241, 0.12)" : "transparent" }}
                        >
                            <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{row.displayLabel ?? row.name}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const LeadDashboard = () => {
    const formatDateForRange = (date: Date) => date.toISOString().split("T")[0];
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 7))
            .toISOString()
            .split("T")[0],
        end: new Date().toISOString().split("T")[0],
    });
    const [filters, setFilters] = useState<any>({
        campaign_id: "all",
        channel_name: "all",
        assignee_id: "all",
        high_priority_only: false,
        qualified_only: false,
        status_type: "all",
        lead_activity: "all",
    });

    const getTeamId = localStorage.getItem(USER_TEAM_ID);
    const [campaigns, setCampaigns] = useState([]);
    const [assignees, setAssignees] = useState([]);
    /** Shared campaign for Leads / Tasks / Team / WhatsApp tab dropdowns */
    const [sectionCampaignId, setSectionCampaignId] = useState<any>("1");
    const [activeTab, setActiveTab] = useState<string>("overview");

    useEffect(() => {
        if (activeTab === "tasks") return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const selectedEnd = new Date(dateRange.end);
        if (Number.isNaN(selectedEnd.getTime())) return;
        selectedEnd.setHours(0, 0, 0, 0);

        if (selectedEnd > today) {
            const start = new Date(today);
            start.setDate(today.getDate() - 7);
            setDateRange({
                start: formatDateForRange(start),
                end: formatDateForRange(today),
            });
        }
    }, [activeTab, dateRange.end]);

    const [kpiData, setKpiData] = useState<any>({
        totalLeads: 0,
        inward: 0,
        outward: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [teamLoading, setTeamLoading] = useState(true);
    const [leadsLoading, setLeadsLoading] = useState(true);
    const [fullscreenChart, setFullscreenChart] = useState(null);
    const [leadsPerCampaignData, setLeadsPerCampaignData] = useState([]);
    const [leadByChannelData, setLeadByChannelData] = useState([]);
    const [engagementData, setEngagementData] = useState([]);
    const [dispositionData, setDispositionData] = useState([]);
    const [leadOpenTrends, setLeadOpenTrends] = useState([]);
    const [completedTasksData, setCompletedTasksData] = useState<any>([]);
    const [funnelData, setFunnelData] = useState<any>([]);
    const [conversionFunnelData, setConversionFunnelData] = useState<any>([]);
    const [funnelConversionRateData, setFunnelConversionRateData] = useState<any>([]);
    const [teamPerformanceData, setTeamPerformanceData] = useState([]);
    const [leadStatusTrends, setLeadStatusTrends] = useState([]);
    const [whatsappTemplateId, setWhatsappTemplateId] = useState<any>("1");
    const [getAllTemplates, setGetAllTemplate] = useState([]);
    const [funnelTooltip, setFunnelTooltip] = useState<{ index: number; x: number; y: number } | null>(null);
    const whatsappHoveredPayloadRef = useRef<{ date: string; graphName: string; chartTitle: string } | null>(null);
    const { data: filterData, isLoading: isFilterLoading } = useGetFilterBundleQuery({});
    const { data: campaignAssigneeData, isLoading: isCampaignAssigneeLoading, status: campaignAssigneeStatus } = useGetFilterAssigneeByCampaignIdQuery({ campaign_id: filters.campaign_id });
    /** Only fetch tab-specific APIs when that tab is active — avoids firing all four on every campaign/date change */
    const skipLeadsCampaignQuery = activeTab !== "leads" || sectionCampaignId === "1";
    const skipTasksCampaignQuery = activeTab !== "tasks";
    const skipTeamCampaignQuery = activeTab !== "team-performance";
    const skipWhatsappCampaignQuery = activeTab !== "whatsapp";
    const { data: campaignLeadAnalysisData, isLoading: isCampaignLeadAnalysisLoading, status: campaignLeadAnalysisStatus } = useGetCampaignLeadAnalysisDataQuery({ campaign_id: sectionCampaignId, start_date: dateRange.start, end_date: dateRange.end }, { skip: skipLeadsCampaignQuery });
    const { data: campaignTasksData, isLoading: isCampaignTasksLoading, status: campaignTasksStatus } = useGetCampaignTasksDataQuery({ campaign_id: sectionCampaignId, start_date: dateRange.start, end_date: dateRange.end }, { skip: skipTasksCampaignQuery });
    const { data: campaignTeamPerformanceData, isLoading: isCampaignTeamPerformanceLoading, status: campaignTeamPerformanceStatus } = useGetCampaignTeamPerformanceDataQuery({ campaign_id: sectionCampaignId, start_date: dateRange.start, end_date: dateRange.end }, { skip: skipTeamCampaignQuery });
    const { data: campaignWhatsappVolumeData, isLoading: isCampaignWhatsappVolumeLoading, status: campaignWhatsappVolumeStatus } = useGetWhatsappVolumeQuery({ campaign_id: sectionCampaignId, team_id: localStorage.getItem("teamId"), start_date: dateRange.start, end_date: dateRange.end, template_id: whatsappTemplateId }, { skip: skipWhatsappCampaignQuery });
    const { data: broadCastMessageData } = useGetBroadcastTemplatesQuery(getTeamId);

    const whatsappChartData = useMemo(() => {
        const raw = campaignWhatsappVolumeData as any;
        if (!raw?.whatsapp_leads_generated?.daily_data) return [];
        const leadsList = raw.whatsapp_leads_generated.daily_data || [];
        const campaignList = (raw.whatsapp_campaign_insight?.daily_data || []).map((d: any) => ({ date: d.date, count: d.count }));
        const deliveredList = (raw.whatsapp_delivered_messages?.daily_data || []).map((d: any) => ({ date: d.date, count: d.count }));
        const readList = (raw.whatsapp_read_messages?.daily_data || []).map((d: any) => ({ date: d.date, count: d.count }));
        const byDate = (list: { date: string; count: number }[]) => Object.fromEntries((list || []).map((d: any) => [String(d.date), Number(d.count ?? 0)]));
        const campaignByDate = byDate(campaignList);
        const deliveredByDate = byDate(deliveredList);
        const readByDate = byDate(readList);
        const formatDateLabel = (dateStr: string) => {
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return dateStr;
            const day = d.getDate();
            const month = d.toLocaleString("en", { month: "short" });
            return `${day} ${month}`;
        };
        return leadsList
            .map((d: any) => {
                const dateStr = String(d.date ?? "");
                return {
                    dateLabel: formatDateLabel(dateStr),
                    date: dateStr,
                    leads: Number(d.count ?? 0),
                    campaign: campaignByDate[dateStr] ?? 0,
                    delivered: deliveredByDate[dateStr] ?? 0,
                    read: readByDate[dateStr] ?? 0,
                };
            })
            .sort((a: any, b: any) => (a.date || "").localeCompare(b.date || ""));
    }, [campaignWhatsappVolumeData]);

    const top5CampaignPerformance = useMemo(() => {
        const list = (leadStatusTrends || []).map((item: any) => ({
            campaign_id: item.campaign_id,
            name: (item.campaign_name || item.name || '').replace(/_/g, ' '),
            value: Number(item.total_leads ?? item.total ?? 0),
        }));
        const sorted = [...list].sort((a, b) => b.value - a.value).slice(0, 5);
        return sorted.map((item, i) => ({ ...item, color: CAMPAIGN_PERFORMANCE_COLORS[i] ?? CAMPAIGN_PERFORMANCE_COLORS[0] }));
    }, [leadStatusTrends]);
    const maxCampaignValue = useMemo(() => Math.max(1, ...top5CampaignPerformance.map((item) => item.value)), [top5CampaignPerformance]);
    const leadChannelNormalized = useMemo(() => {
        const raw = leadByChannelData || [];
        return raw.map((item: any, i: number) => ({
            name: String(item.channel_name ?? item.name ?? "—").replace(/\b\w/g, (c: string) => c.toUpperCase()),
            channelValue: String(item.channel_name ?? item.name ?? "—"),
            value: Number(item.leads_count ?? item.count ?? 0),
            color: LEAD_CHANNEL_COLORS[i % LEAD_CHANNEL_COLORS.length],
            percent_change: item.percent_change ?? item.trend ?? null,
        }));
    }, [leadByChannelData]);
    const leadChannelTotal = useMemo(() => leadChannelNormalized.reduce((s, d) => s + d.value, 0), [leadChannelNormalized]);
    const fetchingTenant = window.location.hostname.split(".")[0];
    const apiPort = process.env.REACT_APP_API_PROTOCOL;
    const API_BASE_URL = `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/common`;
    const navigate = useNavigate();
    const fetchData = async (endpoint, params) => {
        try {
            const token = localStorage.getItem('auth-token');
            const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
                params,
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            console.error(`Error fetching data from ${endpoint}:`, error);
            return [];
        }
    };

    // --- Main Dashboard Component ---
    const loadingContainerStyle = {
        display: 'grid',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
        width: '100%',
        height: '100%'
    };
    // Conversion funnel: API shape { default_stage, stage_name, leads, conversion_rate, percent_change, leads_list? }
    // "Other lead" = total leads minus sum of named (closed) stages — so it shows remainder (e.g. 1 lead, 25%).
    const conversionFunnelNormalized = useMemo(() => {
        const raw = conversionFunnelData || [];
        const totalFromAll = (() => {
            const allRow = raw.find((d: any) => (String(d.stage_name ?? d.default_stage ?? d.funnel_stage ?? "").toLowerCase()) === "all");
            return allRow ? Number(allRow.leads ?? allRow.lead_count ?? 0) : 0;
        })();
        const mapped = raw.map((item: any, i: number) => {
            const name = String(item.name ?? item.stage_name ?? item.default_stage ?? item.funnel_stage ?? "—").replace(/\b\w/g, (c: string) => c.toUpperCase());
            const isAll = name.toLowerCase() === "all";
            return {
                name,
                displayLabel: item.displayLabel ?? (isAll ? "Open, WIP, Reopen" : name),
                value: Number(item.value ?? item.leads ?? item.lead_count ?? 0),
                conversion_rate: Number(item.conversion_rate ?? 0),
                percent_change: Number(item.percent_change ?? 0),
                leads_list: item.leads_list || [],
                color: item.color ?? getFunnelColor(i),
                status_type: item.status_type ?? "closed",
            };
        });
        const total = totalFromAll > 0 ? totalFromAll : mapped.reduce((s, d) => s + d.value, 0);
        const sumOtherStages = mapped.filter((d: any) => d.name.toLowerCase() !== "all").reduce((s, d) => s + d.value, 0);
        const otherLeadValue = Math.max(0, total - sumOtherStages);
        const otherLeadRate = total > 0 ? Math.round((otherLeadValue / total) * 100 * 100) / 100 : 0;
        return mapped.map((d: any) => {
            if (d.name.toLowerCase() === "all") {
                // Don't show API percent_change for "Other lead" — it refers to total leads, not this segment.
                return { ...d, value: otherLeadValue, conversion_rate: otherLeadRate, percent_change: 0 };
            }
            return d;
        });
    }, [conversionFunnelData]);
    const conversionFunnelTotal = useMemo(() => {
        const raw = conversionFunnelData || [];
        const hasStatusType = raw.some((d: any) => d.status_type != null && d.status_type !== "");
        if (hasStatusType && raw.length > 0) {
            return raw.reduce((s: number, d: any) => s + Number(d.lead_count ?? d.value ?? d.leads ?? 0), 0);
        }
        const allRow = raw.find((d: any) => (String(d.stage_name ?? d.default_stage ?? d.funnel_stage ?? "").toLowerCase()) === "all");
        if (allRow) return Number(allRow.leads ?? allRow.lead_count ?? allRow.value ?? 0);
        return conversionFunnelNormalized.reduce((s, d) => s + d.value, 0);
    }, [conversionFunnelData, conversionFunnelNormalized]);

    /** Main pie: Active, Total WIP, Total Closed, Reopen – colors/labels from CONVERSION_ENGAGEMENT_CONFIG (same as EmailPieChart). Includes percentage for chart labels. */
    const conversionFunnelMainPieData = useMemo(() => {
        const raw = conversionFunnelData || [];
        const hasStatusType = raw.some((d: any) => d.status_type != null && d.status_type !== "");
        if (!hasStatusType || raw.length === 0) return null;
        const getVal = (d: any) => Number(d.value ?? d.leads ?? d.lead_count ?? 0);
        const configByKey: Record<string, { label: string; color: string }> = {};
        CONVERSION_ENGAGEMENT_CONFIG.forEach((c) => { configByKey[c.key] = { label: c.label, color: c.color }; });
        const groups: Record<string, { value: number }> = {
            active: { value: 0 },
            wip: { value: 0 },
            closed: { value: 0 },
            reopen: { value: 0 },
        };
        raw.forEach((d: any) => {
            const st = (d.status_type || "").toLowerCase();
            const v = getVal(d);
            if (st === "active") groups.active.value += v;
            else if (st === "wip") groups.wip.value += v;
            else if (st === "closed") groups.closed.value += v;
            else if (st === "reopen") groups.reopen.value += v;
        });
        const total = groups.active.value + groups.wip.value + groups.closed.value + groups.reopen.value;
        const pct = (v: number) => total > 0 ? Math.round((v / total) * 100 * 100) / 100 : 0;
        const wipCount = raw.filter((d: any) => (d.status_type || "").toLowerCase() === "wip").length;
        const closedCount = raw.filter((d: any) => (d.status_type || "").toLowerCase() === "closed").length;
        const minClickable = 0.01;
        const minShow = 0.01;
        const activeConfig = configByKey.active;
        const wipConfig = configByKey.wip;
        const closedConfig = configByKey.closed;
        const reopenConfig = configByKey.reopen;
        return [
            { name: "Active", displayLabel: activeConfig?.label ?? "Active", value: groups.active.value > 0 ? groups.active.value : minShow, percentage: pct(groups.active.value), status_type: "active", color: activeConfig?.color ?? "#10b981", leadCount: Math.round(groups.active.value), isAggregate: false },
            { name: "Total WIP", displayLabel: wipConfig?.label ?? "WIP", value: groups.wip.value > 0 ? groups.wip.value : (wipCount > 0 ? minClickable : minShow), percentage: pct(groups.wip.value), status_type: "wip", color: wipConfig?.color ?? "#ef4444", leadCount: Math.round(groups.wip.value), isAggregate: true },
            { name: "Total Closed", displayLabel: closedConfig?.label ?? "Closed", value: groups.closed.value > 0 ? groups.closed.value : (closedCount > 0 ? minClickable : minShow), percentage: pct(groups.closed.value), status_type: "closed", color: closedConfig?.color ?? "#f59e0b", leadCount: Math.round(groups.closed.value), isAggregate: true },
            { name: "Reopen", displayLabel: reopenConfig?.label ?? "Reopen", value: groups.reopen.value > 0 ? groups.reopen.value : minShow, percentage: pct(groups.reopen.value), status_type: "reopen", color: reopenConfig?.color ?? "#6366f1", leadCount: Math.round(groups.reopen.value), isAggregate: false },
        ];
    }, [conversionFunnelData]);

    const [selectedConversionSegment, setSelectedConversionSegment] = useState<"wip" | "closed" | null>(null);

    /** Sub-pie: WIP or Closed sub-stages (when user clicks Total WIP or Total Closed). */
    const conversionFunnelSubPieData = useMemo(() => {
        const raw = conversionFunnelData || [];
        if (!selectedConversionSegment || (selectedConversionSegment !== "wip" && selectedConversionSegment !== "closed")) return [];
        const st = selectedConversionSegment;
        const filtered = raw.filter((d: any) => (d.status_type || "").toLowerCase() === st);
        const colorOffset = st === "wip" ? 1 : 5;
        return filtered.map((d: any, index: number) => {
            const stageName = d.funnel_stage ?? d.name ?? d.stage_name ?? d.default_stage ?? "—";
            const displayLabel = typeof stageName === "string" ? stageName.replace(/\b\w/g, (c: string) => c.toUpperCase()) : String(stageName);
            const val = Number(d.lead_count ?? d.value ?? d.leads ?? 0);
            return {
                name: stageName,
                displayLabel: d.displayLabel ?? displayLabel,
                value: val > 0 ? val : 0.01,
                status_type: d.status_type,
                color: d.color ?? getFunnelColor(colorOffset + index),
            };
        });
    }, [conversionFunnelData, selectedConversionSegment]);

    /** Data for ExplodingPieChart in Conversion engagement: main pie + subData for WIP/Closed. Includes percentage and lead count for labels. */
    const conversionExplodingMainData = useMemo((): import("./ExplodingPieChart").ExplodingPieDataItem[] => {
        const main = conversionFunnelMainPieData ?? conversionFunnelNormalized;
        if (!main || main.length === 0) return [];
        const raw = conversionFunnelData || [];
        const total = main.reduce((s: number, r: any) => s + Number(r.value ?? 0), 0);
        const pctFromValue = (v: number) => (total > 0 ? Math.round((v / total) * 100 * 100) / 100 : undefined);
        return main.map((row: any) => {
            const category = row.displayLabel ?? row.name ?? String(row.status_type ?? "");
            const value = Number(row.value ?? 0) || 0.01;
            const leadCount = row.leadCount != null ? row.leadCount : Math.round(Number(row.value ?? 0));
            const color = row.color;
            const percentage = row.percentage != null ? Number(row.percentage) : pctFromValue(value);
            let subData: import("./ExplodingPieChart").ExplodingPieSubItem[] = [];
            if (row.status_type === "wip" || row.status_type === "closed") {
                const st = (row.status_type || "").toLowerCase();
                const filtered = raw.filter((d: any) => (d.status_type || "").toLowerCase() === st);
                const colorOffset = st === "wip" ? 1 : 5;
                subData = filtered.map((d: any, index: number) => {
                    const stageName = d.funnel_stage ?? d.name ?? d.stage_name ?? d.default_stage ?? "—";
                    const displayLabel = typeof stageName === "string" ? stageName.replace(/\b\w/g, (c: string) => c.toUpperCase()) : String(stageName);
                    const val = Number(d.lead_count ?? d.value ?? d.leads ?? 0);
                    return {
                        category: d.displayLabel ?? displayLabel,
                        value: val > 0 ? val : 0.01,
                        leadCount: val,
                        percentage: d.percentage != null ? Number(d.percentage) : undefined,
                        color: d.color ?? getFunnelColor(colorOffset + index),
                        status_type: row.status_type,
                        valueForApi: String(stageName),
                    };
                });
            }
            return { category, value, leadCount, percentage, color, subData, status_type: row.status_type };
        });
    }, [conversionFunnelMainPieData, conversionFunnelNormalized, conversionFunnelData]);

    /** Drilldown connecting lines: ref and size for the Conversion engagement chart row (parent + child pies). */
    const conversionEngagementRowRef = useRef<HTMLDivElement>(null);
    const [conversionEngagementRowSize, setConversionEngagementRowSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = conversionEngagementRowRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const rect = el.getBoundingClientRect();
            setConversionEngagementRowSize({ width: rect.width, height: rect.height });
        });
        ro.observe(el);
        const rect = el.getBoundingClientRect();
        setConversionEngagementRowSize({ width: rect.width, height: rect.height });
        return () => ro.disconnect();
    }, [selectedConversionSegment, conversionFunnelSubPieData.length]);

    /** Line coordinates for drilldown: from selected parent slice edges to child donut top/bottom. Recharts angle 0 = 3 o'clock, clockwise. */
    const conversionDrilldownLines = useMemo(() => {
        if (!selectedConversionSegment || selectedConversionSegment !== "wip" && selectedConversionSegment !== "closed") return null;
        const mainData = conversionFunnelMainPieData;
        if (!mainData || mainData.length === 0 || conversionFunnelSubPieData.length === 0) return null;
        const total = mainData.reduce((s: number, d: any) => s + Number(d.value ?? 0), 0);
        if (total <= 0) return null;
        const idx = selectedConversionSegment === "wip" ? 1 : 2;
        const row = mainData[idx];
        if (!row) return null;
        let startDeg = 0;
        for (let i = 0; i < idx; i++) startDeg += (Number(mainData[i]?.value ?? 0) / total) * 360;
        const sliceDeg = (Number(row.value ?? 0) / total) * 360;
        const endDeg = startDeg + sliceDeg;
        const w = conversionEngagementRowSize.width;
        const h = conversionEngagementRowSize.height;
        if (w <= 0 || h <= 0) return null;
        const gap = 16;
        const halfW = (w - gap) / 2;
        const parentCx = halfW / 2;
        const parentCy = h / 2;
        const parentRadius = 0.85 * Math.min(halfW, 260) / 2;
        const childCx = halfW + gap + halfW / 2;
        const childCy = h / 2;
        const childRadius = 0.8 * Math.min(halfW, 220) / 2;
        const deg2rad = Math.PI / 180;
        const x1 = parentCx + parentRadius * Math.cos(startDeg * deg2rad);
        const y1 = parentCy - parentRadius * Math.sin(startDeg * deg2rad);
        const x2 = parentCx + parentRadius * Math.cos(endDeg * deg2rad);
        const y2 = parentCy - parentRadius * Math.sin(endDeg * deg2rad);
        const childTopY = childCy - childRadius;
        const childBottomY = childCy + childRadius;
        return {
            line1: { x1, y1, x2: childCx, y2: childTopY },
            line2: { x1: x2, y1: y2, x2: childCx, y2: childBottomY },
            color: row.color ?? "#64748b",
        };
    }, [selectedConversionSegment, conversionFunnelMainPieData, conversionFunnelSubPieData.length, conversionEngagementRowSize]);

    const [funnelLeadsModalOpen, setFunnelLeadsModalOpen] = useState(false);
    const [clickToExpandPage, setClickToExpandPage] = useState(1);
    /** Params for click-to-expand API: title, graph_name, value, campaign_id or team_id (WhatsApp), optional status_type, optional template_id (WhatsApp graphs). */
    const [funnelLeadsModalStage, setFunnelLeadsModalStage] = useState<{ title: string; graph_name: string; value: string; campaign_id?: string; team_id?: string | null; status_type?: string; template_id?: string | number | null } | null>(null);

    const hasClickToExpandParams = funnelLeadsModalStage && funnelLeadsModalOpen && (funnelLeadsModalStage.campaign_id || funnelLeadsModalStage.team_id != null);
    const clickToExpandParams = hasClickToExpandParams && funnelLeadsModalStage
        ? {
            start_date: dateRange.start,
            end_date: dateRange.end,
            campaign_id: funnelLeadsModalStage.campaign_id ?? "",
            graph_name: funnelLeadsModalStage.graph_name,
            value: funnelLeadsModalStage.value,
            ...(funnelLeadsModalStage.status_type != null && { status_type: funnelLeadsModalStage.status_type }),
            ...(funnelLeadsModalStage.team_id != null && funnelLeadsModalStage.team_id !== "" && { team_id: funnelLeadsModalStage.team_id }),
            ...(funnelLeadsModalStage.template_id != null && funnelLeadsModalStage.template_id !== "" && { template_id: funnelLeadsModalStage.template_id }),
            page: clickToExpandPage,
            page_size: 10,
        }
        : null;
    const { data: clickToExpandData, isLoading: isClickToExpandLoading } = useGetLeadClickToExpandQuery(clickToExpandParams ?? { start_date: "", end_date: "", campaign_id: "", graph_name: "", value: "" }, { skip: !clickToExpandParams });
    const clickToExpandRows = useMemo(() => {
        if (!clickToExpandData) return [];
        const raw = (clickToExpandData as any)?.results ?? (clickToExpandData as any)?.data ?? clickToExpandData;
        return Array.isArray(raw) ? raw : [];
    }, [clickToExpandData]);
    const clickToExpandPagination = useMemo(() => {
        const d = clickToExpandData as any;
        if (!d) return null;
        const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
        const totalPages = d.total_pages ?? 1;
        const page = d.page ?? clickToExpandPage;
        const pageSize = d.page_size ?? 10;
        return { totalCount: count, currentPage: page, totalPages, pageSize };
    }, [clickToExpandData, clickToExpandPage]);

    useEffect(() => {
        if (funnelLeadsModalOpen) setClickToExpandPage(1);
    }, [funnelLeadsModalStage?.graph_name, funnelLeadsModalStage?.value, funnelLeadsModalStage?.campaign_id, funnelLeadsModalStage?.team_id]);

    useEffect(() => {
        if (filterData) {
            setCampaigns(filterData.campaigns || []);
            setSectionCampaignId(filterData.campaigns[0]?.id);
        }

    }, [filterData]);

    useEffect(() => {
        if (campaignAssigneeData) {
            setAssignees(campaignAssigneeData.assignees);
        }
    }, [campaignAssigneeData]);

    useEffect(() => {
        if (campaignLeadAnalysisData?.funnel) {
            const filteredFunnel = campaignLeadAnalysisData.funnel.filter(
                (item: any) => (item.lead_count ?? item.leads ?? 0) > 0
            );
            setFunnelData(filteredFunnel);
            setFunnelConversionRateData(filteredFunnel);
            setConversionFunnelData(filteredFunnel)
            console.log(funnelConversionRateData, "test");
        } else {
            setFunnelData([]);
            setFunnelConversionRateData([]);
        }


    }, [campaignLeadAnalysisData]);

    // Close Conversion engagement child chart (WIP/Closed sub-pie) when data or filters change
    useEffect(() => {
        setSelectedConversionSegment(null);
    }, [conversionFunnelData]);

    useEffect(() => {
        if (campaignTasksData) {
            setCompletedTasksData(campaignTasksData);
        }
    }, [campaignTasksData]);
    useEffect(() => {
        if (campaignTeamPerformanceData) {
            setTeamPerformanceData(campaignTeamPerformanceData);
        }
    }, [campaignTeamPerformanceData]);


    useEffect(() => {
        if (campaignTeamPerformanceStatus === "pending") {
            setTeamLoading(true);
        }
        else {
            setTeamLoading(false);
        }
    }, [campaignTeamPerformanceStatus]);



    useEffect(() => {
        if (broadCastMessageData) {
            setGetAllTemplate(broadCastMessageData?.results);
            setWhatsappTemplateId(broadCastMessageData?.results[0]?.id);
        }
    }, [broadCastMessageData]);
    // Task status breakdown chart – modern enterprise SaaS design
    const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
        active: { label: "Active", color: "#6366f1" },
        rescheduled: { label: "Rescheduled", color: "#f59e0b" },
        cancelled: { label: "Cancelled", color: "#ef4444" },
        done: { label: "Done", color: "#10b981" },
        "Due Today": { label: "Due Today", color: "#6366f1" },
        Upcoming: { label: "Upcoming", color: "#8b5cf6" },
        Overdue: { label: "Overdue", color: "#ef4444" },
    };

    console.log(conversionFunnelNormalized, "conversionFunnelNormalized");
    const CompletedTasksOverTimeBarChart = ({ data, dateRange, onStatusClick }: { data: any; dateRange?: { start: string; end: string }; onStatusClick?: (name: string, leads_list: any[]) => void }) => {
        const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

        // Handle empty data
        if (!data || !data.status_counts || typeof data.status_counts !== "object") {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "280px",
                        padding: "32px",
                        color: "#64748b",
                        fontSize: "14px",
                        textAlign: "center",
                    }}
                >
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <span style={{ fontSize: "24px" }}>📋</span>
                    </div>
                    <div style={{ fontWeight: 600, color: "#475569", marginBottom: "4px" }}>No task data yet</div>
                    <div>Tasks will appear here when the selected campaign has activity.</div>
                </div>
            );
        }

        const totalTasks = Number(data.total_tasks) || 0;
        const chartData = Object.entries(data.status_counts).map(([status, value]: [string, any]) => {
            const conf = STATUS_CONFIG[status] || { label: status, color: "#6366f1" };
            const num = typeof value === "object" && value != null && "count" in value ? Number(value.count) || 0 : Number(value) || 0;
            const lead_list = typeof value === "object" && value != null && Array.isArray(value.lead_list) ? value.lead_list : [];
            const pct = totalTasks > 0 ? Math.round((num / totalTasks) * 100) : 0;
            return {
                status: conf.label,
                statusKey: status,
                count: num,
                lead_list,
                color: conf.color,
                percentage: pct,
            };
        });

        const timeRangeLabel = dateRange
            ? `${dateRange.start} – ${dateRange.end}`
            : "";

        return (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: "320px" }}>
                {/* Header: campaign name + time range */}


                {/* Bar chart */}
                <div style={{ flex: 1, minHeight: "220px", position: "relative" }}>
                    <ResponsiveContainerC width="100%" height={240}>
                        <BarChartC
                            data={chartData}
                            margin={{ top: 12, right: 16, bottom: 28, left: 8 }}
                            barCategoryGap="28%"
                            onMouseMove={(state: any) => {
                                if (state?.activeTooltipIndex !== undefined) setHoveredIndex(state.activeTooltipIndex);
                            }}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            <CartesianGridC stroke="#f1f5f9" strokeDasharray="0" vertical={false} />
                            <XAxisC
                                dataKey="status"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }}
                                interval={0}
                            />
                            <YAxisC
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }}
                                width={28}
                            />
                            <TooltipC
                                content={({ active, payload }: any) => {
                                    if (active && payload?.length) {
                                        const item = payload[0].payload;
                                        return (
                                            <div
                                                style={{
                                                    backgroundColor: "#fff",
                                                    padding: "12px 16px",
                                                    borderRadius: "12px",
                                                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                                                    border: "1px solid #e2e8f0",
                                                    minWidth: "120px",
                                                }}
                                            >
                                                <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b", marginBottom: "4px" }}>
                                                    {item.status}
                                                </div>
                                                <div style={{ fontSize: "20px", fontWeight: 700, color: item.color }}>
                                                    {item.count}
                                                </div>
                                                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                                                    {item.percentage}% of total
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                                cursor={{ fill: "rgba(99, 102, 241, 0.06)", radius: 8 }}
                            />
                            <BarC
                                dataKey="count"
                                name="Tasks"
                                radius={[8, 8, 0, 0]}
                                maxBarSize={56}
                                animationBegin={0}
                                animationDuration={600}
                                animationEasing="ease-out"
                                isAnimationActive
                                onClick={(_data: any, index: number) => {
                                    const entry = chartData[index];
                                    if (entry && onStatusClick) onStatusClick(entry.status, entry.lead_list || []);
                                }}
                                style={{ cursor: onStatusClick ? "pointer" : "default" }}
                            >
                                {chartData.map((entry, index) => (
                                    <CellC
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        style={{
                                            filter: hoveredIndex === index ? "brightness(1.05)" : "none",
                                            transition: "filter 0.15s ease",
                                        }}
                                    />
                                ))}
                            </BarC>
                        </BarChartC>
                    </ResponsiveContainerC>
                </div>

                {/* Task legends at bottom */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px 24px",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingTop: "12px",
                        marginTop: "8px",
                        borderTop: "1px solid #e2e8f0",
                    }}
                >
                    {chartData.map((entry) => (
                        <div
                            key={entry.statusKey}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "6px 12px",
                                borderRadius: "8px",
                            }}
                        >
                            <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: entry.color, flexShrink: 0 }} />
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{entry.status}</span>

                        </div>
                    ))}
                </div>
            </div>
        );
    };
    // Closure rate color: green (high), amber (medium), red (low)
    const getConversionColor = (value: number) => {
        if (value >= 60) return "#10b981";
        if (value >= 30) return "#f59e0b";
        return "#ef4444";
    };

    // X-axis tick for assignee names: same as other charts – horizontal, 1–3 lines, underscores as spaces
    const AssigneeConversionXAxisTick = (props: any) => {
        const { x, y, payload, dataLength } = props;
        const n = dataLength ?? 0;
        const [labelGap, setLabelGap] = useState(() => getLeadTrendsLabelGap());
        useEffect(() => {
            const onResize = () => setLabelGap(getLeadTrendsLabelGap());
            window.addEventListener('resize', onResize);
            return () => window.removeEventListener('resize', onResize);
        }, []);
        const value = (payload?.value ?? '').trim().replace(/_/g, ' ');
        const maxChars = n <= 3 ? 999 : n >= 6 ? 10 : n >= 4 ? 12 : LEAD_TRENDS_LABEL_MAX_CHARS;
        const maxLines = n > 4 ? 3 : 2;
        const lines = splitCampaignLabel(value, maxChars, maxLines);
        const fill = '#64748b';
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={labelGap} textAnchor="middle" fill={fill} fontSize={LEAD_TRENDS_LABEL_FONT_SIZE} fontWeight={500} style={{ fontFamily: 'inherit' }}>
                    {lines.length === 1 ? (
                        lines[0]
                    ) : lines.length === 2 ? (
                        <>
                            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
                            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
                        </>
                    ) : (
                        <>
                            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
                            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
                            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[2]}</tspan>
                        </>
                    )}
                </text>
            </g>
        );
    };

    const AssigneeConversionRateBarChart = ({ data, onBarClick }: { data: any[]; onBarClick?: (assigneeId: string | number, assigneeName: string) => void }) => {
        if (!data || data.length === 0) {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "280px",
                        padding: "32px",
                        color: "#64748b",
                        fontSize: "14px",
                        textAlign: "center",
                    }}
                >
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <span style={{ fontSize: "24px" }}>📊</span>
                    </div>
                    <div style={{ fontWeight: 600, color: "#475569", marginBottom: "4px" }}>No closure rate data yet</div>
                    <div>Assignee closure rates will appear here when data is available.</div>
                </div>
            );
        }

        const chartData = data.map((assignee: any, index: number) => ({
            ...assignee,
            conversion_rate_percent: assignee.closure_rate ?? 0,
            assignee_id: assignee.assignee_id ?? assignee.id ?? String(index),
        }));

        return (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: "300px" }}>
                <ResponsiveContainerC width="100%" height={300}>
                    <BarChartC
                        data={chartData}
                        margin={{
                            top: 28,
                            right: 52,
                            bottom: chartData.length > 4 ? 64 : 56,
                            left: chartData.length >= 6 ? 44 : 36,
                        }}
                        barCategoryGap={chartData.length >= 8 ? "32%" : chartData.length > 6 ? "30%" : chartData.length > 4 ? "28%" : "28%"}
                    >
                        <CartesianGridC stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                        <XAxisC
                            dataKey="team_member"
                            axisLine={false}
                            tickLine={false}
                            tick={<AssigneeConversionXAxisTick dataLength={chartData.length} />}
                            tickMargin={8}
                            interval={0}
                            height={chartData.length > 4 ? 64 : 48}
                        />
                        <YAxisC
                            axisLine={false}
                            tickLine={false}
                            domain={[0, 100]}
                            ticks={[0, 25, 50, 75, 100]}
                            tickFormatter={(val: number) => `${val}%`}
                            tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }}
                            width={40}
                        />
                        <TooltipC
                            content={({ active, payload, label }: any) => {
                                if (!active || !payload?.length) return null;
                                const value = payload[0].value;
                                const color = getConversionColor(Number(value));
                                return (
                                    <div
                                        style={{
                                            backgroundColor: "#fff",
                                            padding: "12px 16px",
                                            borderRadius: "12px",
                                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                                            border: "1px solid #e2e8f0",
                                            minWidth: "140px",
                                        }}
                                    >
                                        <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{(label ?? '').replace(/_/g, ' ')}</div>
                                        <div style={{ fontSize: "20px", fontWeight: 700, color }}>{value != null ? Number(value).toFixed(1) : "0"}%</div>
                                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>Closure rate</div>
                                    </div>
                                );
                            }}
                            cursor={{ fill: "rgba(16, 185, 129, 0.06)", radius: 6 }}
                        />
                        <BarC
                            dataKey="conversion_rate_percent"
                            name="Closure Rate"
                            radius={[8, 8, 0, 0]}
                            maxBarSize={48}
                            animationBegin={0}
                            animationDuration={600}
                            animationEasing="ease-out"
                            onClick={onBarClick ? (data: any) => data?.assignee_id != null && onBarClick(data.assignee_id, data.team_member ?? "") : undefined}
                            style={{ cursor: onBarClick ? "pointer" : undefined }}
                            label={({ value, x, y, width }: any) => {
                                const num = Number(value) ?? 0;
                                const pct = `${num.toFixed(1)}%`;
                                const fillColor = num > 0 ? "#10b981" : "#64748b";
                                return (
                                    <text x={x + width / 2} y={(y ?? 0) - 8} fill={fillColor} textAnchor="middle" fontSize={12} fontWeight={600}>
                                        {pct}
                                    </text>
                                );
                            }}
                        >
                            {chartData.map((entry: any, index: number) => (
                                <CellC key={`cell-${index}`} fill={getConversionColor(Number(entry.conversion_rate_percent))} />
                            ))}
                        </BarC>
                    </BarChartC>
                </ResponsiveContainerC>
            </div>
        );
    };



    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {
                start_date: dateRange.start,
                end_date: dateRange.end,
                ...filters,
            };
            const [
                overviewRes,
            ] = await Promise.all([
                fetchData("/leadDashboard/overview-bundle/", params)
            ]);
            const safeKpiRes = overviewRes
            setKpiData({
                totalLeads: safeKpiRes.total_leads || 0,
                inward: safeKpiRes.inwards_leads || 0,
                outward: safeKpiRes.outward_leads || 0,
            });
            setEngagementData(overviewRes.engagement || []);
            setDispositionData(overviewRes.disposition || []);
            setLeadStatusTrends(safeKpiRes?.leadsTrend || []);
            // Normalize leads per campaign for chart: { campaign_name, leads_count, campaign_id? }
            const rawLeadsPerCampaign = safeKpiRes.leads_per_campaign || [];
            setLeadsPerCampaignData(
                rawLeadsPerCampaign.map((item: any) => ({
                    campaign_id: item.campaign_id ?? item.id,
                    campaign_name: item.campaign_name ?? item.name ?? item.campaign ?? '',
                    leads_count: Number(item.leads_count ?? item.count ?? item.total ?? 0),
                }))
            );
            // Normalize open leads per campaign for chart: { campaign_name, open_leads_count, campaign_id? }
            const rawOpenLeads = safeKpiRes?.openLeadsTrend || safeKpiRes?.open_leads_per_campaign || [];
            setLeadOpenTrends(
                rawOpenLeads.map((item: any) => ({
                    campaign_id: item.campaign_id,
                    campaign_name: item.campaign_name ?? item.name ?? item.campaign ?? '',
                    open_leads_count: Number(item.open_leads_count ?? item.open_count ?? item.count ?? 0),
                }))
            );

            setLeadByChannelData(safeKpiRes?.leadsbyChannel || []);

        } catch (error) {
            console.error("Error loading dashboard data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [dateRange, filters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (campaignLeadAnalysisStatus === "pending") {
            setLeadsLoading(true);
        }
        else {
            setLeadsLoading(false);
        }
    }, [campaignLeadAnalysisStatus]);


    const hasFunnelData = funnelData?.some(
        (item) => item.lead_count > 0
    );

    /** Enrich leads-per-campaign with campaign_id from campaigns list when API omits it (so bar click can open modal). */
    const leadsPerCampaignDataWithIds = useMemo(() => {
        const list = leadsPerCampaignData || [];
        const campList = campaigns || [];
        const norm = (s: string) => String(s || "").trim().toLowerCase();
        return list.map((item: any, index: number) => {
            const exactId = item.campaign_id ?? item.id;
            const matchById = exactId !== undefined && exactId !== null ? exactId : null;
            const matchByName = campList.find((c: any) =>
                norm(c.name) === norm(item.campaign_name) || norm(c.campaign_name) === norm(item.campaign_name)
            )?.id;
            const matchByIndex = campList[index]?.id;
            const id = matchById ?? matchByName ?? matchByIndex ?? exactId;
            return { ...item, campaign_id: id };
        });
    }, [leadsPerCampaignData, campaigns]);

    console.log("leadStatusTrends", leadStatusTrends);

    return (
        <div className="dashboard-page">
            {/* Header Section */}
            <div
                className="dashboard-page__header"
                style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                    border: "1px solid #e2e8f0",
                    padding: "20px 24px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "16px",
                    }}  
                >
                    {/* Title */}
                    <div>
                        <h2
                        >
                            Lead dashboard
                        </h2>
                        <p
                            style={{
                                fontSize: "14px",
                                color: "#64748b",
                                margin: 0,
                            }}
                        >
                            Monitor your lead performance and analytics
                        </p>
                    </div>

                    {/* Filters */}
                    <div
                        style={{
                            display: "flex",
                            gap: "12px",
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        {/* Date Range Picker */}
                        <div style={{ position: "relative" }}>
                            <CustomDateRange
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                allowFutureDates={activeTab === "tasks"}
                            />

                        </div>

                        {/* Team Select */}
                        <div style={{ position: "relative", minWidth: "200px" }}>
                            <label
                                style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "12px",
                                    backgroundColor: "#ffffff",
                                    padding: "0 4px",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#64748b",
                                    zIndex: 1,
                                }}
                            >
                                Campaign
                            </label>
                            <select
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#1e293b",
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "8px",
                                    outline: "none",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onChange={(e) =>
                                    setFilters({ ...filters, campaign_id: e.target.value })
                                }
                                value={filters.campaign_id}

                                onFocus={(e) => {
                                    e.target.style.borderColor = "#6366f1";
                                    e.target.style.boxShadow =
                                        "0 0 0 3px rgba(99, 102, 241, 0.15)";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#e2e8f0";
                                    e.target.style.boxShadow = "none";
                                }}
                            >
                                <option value="all" selected>    All Campaigns</option>
                                {campaigns.map((campaign: any) => (
                                    <option key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Workflow Select */}
                        <div style={{ position: "relative", minWidth: "200px" }}>
                            <label
                                style={{
                                    position: "absolute",
                                    top: "-8px",
                                    left: "12px",
                                    backgroundColor: "#ffffff",
                                    padding: "0 4px",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#64748b",
                                    zIndex: 1,
                                }}
                            >
                                Assignee
                            </label>
                            <select
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#1e293b",
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "8px",
                                    outline: "none",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                                onChange={(e) =>
                                    setFilters({ ...filters, assignee_id: e.target.value })
                                }
                                value={filters.assignee_id}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#6366f1";
                                    e.target.style.boxShadow =
                                        "0 0 0 3px rgba(99, 102, 241, 0.15)";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#e2e8f0";
                                    e.target.style.boxShadow = "none";
                                }}
                            >
                                <option value="all" selected>All Assignees</option>
                                {assignees?.map((assignee) => (
                                    <option key={assignee.id} value={assignee.id}>
                                        {capitalizeFirstLetter(assignee.username.split("@")[0])}
                                    </option>
                                ))}
                            </select>
                        </div>



                    </div>
                </div>
            </div>
            {isLoading ? (
                <div className="dashboard-page__content" style={loadingContainerStyle}>
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <div className="dashboard-page__subnav">
                    {/* KPI Cards Row */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "24px",
                            marginBottom: "24px",
                        }}
                    >
                        {/* Card 1: Total Leads */}
                        <div
                            style={{
                                backgroundColor: "#eef2ff",
                                borderRadius: "18px",
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                                padding: "24px",
                                position: "relative",
                                minHeight: "140px",
                            }}
                        >
                            <div
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "12px",
                                    backgroundColor: "rgba(99, 102, 241, 0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "12px",
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", letterSpacing: "0.05em", marginBottom: "4px" }}>
                                Total leads
                            </div>
                            <div style={{ fontSize: "28px", fontWeight: "700", color: "#1e293b", letterSpacing: "-0.02em" }}>
                                {kpiData.totalLeads?.toLocaleString() || '0'}
                            </div>
                        </div>

                        {/* Card 2: Inward Leads */}
                        <div
                            style={{
                                backgroundColor: "#f0fdf4",
                                borderRadius: "18px",
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                                padding: "24px",
                                position: "relative",
                                minHeight: "140px",
                            }}
                        >
                            <div
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "12px",
                                    backgroundColor: "rgba(22, 163, 74, 0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "12px",
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                    stroke="#16a34a" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="17" y1="7" x2="7" y2="17" />
                                    <polyline points="7 9 7 17 15 17" />
                                </svg>
                            </div>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", letterSpacing: "0.05em", marginBottom: "4px" }}>
                                Inbound leads
                            </div>
                            <div style={{ fontSize: "28px", fontWeight: "700", color: "#1e293b", letterSpacing: "-0.02em" }}>
                                {kpiData.inward?.toLocaleString() || '0'}
                            </div>
                        </div>

                        {/* Card 3: Outward Leads */}
                        <div
                            style={{
                                backgroundColor: "#fef2f2",
                                borderRadius: "18px",
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                                padding: "24px",
                                position: "relative",
                                minHeight: "140px",
                            }}
                        >
                            <div
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "12px",
                                    backgroundColor: "rgba(220, 38, 38, 0.15)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: "12px",
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M7 17L17 7" />
                                    <path d="M7 7h10v10" />
                                </svg>
                            </div>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", letterSpacing: "0.05em", marginBottom: "4px" }}>
                                Outreach leads
                            </div>
                            <div style={{ fontSize: "28px", fontWeight: "700", color: "#1e293b", letterSpacing: "-0.02em" }}>
                                {kpiData.outward?.toLocaleString() || '0'}
                            </div>
                        </div>
                    </div>

                    {/* Tabs Navigation */}
                    <div
                        className="tab-container dashboard-page__tabs"
                        style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                            border: "1px solid #e2e8f0",
                            marginBottom: "24px",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-end",
                                gap: "0",
                                padding: "0 24px",
                                overflowX: "auto",
                            }}
                        >
                            {[
                                { id: "overview", label: "Overview", icon: "grid" },
                                { id: "leads", label: "Leads", icon: "users" },

                                { id: "tasks", label: "Tasks", icon: "checklist" },
                                { id: "team-performance", label: "Team", icon: "chart" },
                                { id: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
                            ].map((tab) => {
                                const isActive = activeTab === tab.id;
                                const color = isActive ? "#4f46e5" : "#64748b";
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            padding: "16px 20px 14px",
                                            marginBottom: "-1px",
                                            border: "none",
                                            borderBottom: isActive ? "2px solid #4f46e5" : "2px solid transparent",
                                            background: "none",
                                            cursor: "pointer",
                                            color,
                                            fontSize: "14px",
                                            fontWeight: isActive ? 600 : 400,
                                            fontFamily: "inherit",
                                            whiteSpace: "nowrap",
                                            transition: "color 0.15s ease, border-color 0.15s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.color = "#475569";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.color = "#64748b";
                                            }
                                        }}
                                    >
                                        {tab.icon === "grid" && (
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                <rect x="3" y="3" width="7" height="7" />
                                                <rect x="14" y="3" width="7" height="7" />
                                                <rect x="3" y="14" width="7" height="7" />
                                                <rect x="14" y="14" width="7" height="7" />
                                            </svg>
                                        )}
                                        {tab.icon === "users" && (
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                        )}
                                        {tab.icon === "checklist" && (
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                <path d="M9 11l3 3L22 4" />
                                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                            </svg>
                                        )}
                                        {tab.icon === "whatsapp" && (
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                            </svg>
                                        )}
                                        {tab.icon === "chart" && (
                                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                <line x1="18" y1="20" x2="18" y2="10" />
                                                <line x1="12" y1="20" x2="12" y2="4" />
                                                <line x1="6" y1="20" x2="6" y2="14" />
                                            </svg>
                                        )}
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div
                            style={{
                                height: "1px",
                                backgroundColor: "#e2e8f0",
                                width: "100%",
                            }}
                        />
                    </div>
                    </div>

                    <div className="dashboard-page__content">
                    {/* Overview Tab Content */}
                    {activeTab === 'overview' && (
                        <>
                            <div

                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 380px",
                                    gap: "24px",
                                    marginBottom: "24px",
                                }}
                            >
                                {/* Left Card – Lead Trends */}
                                <div
                                    style={{
                                        backgroundColor: "#ffffff",
                                        borderRadius: "18px",
                                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                                        border: "1px solid #e2e8f0",
                                        padding: "24px",
                                        minHeight: "360px",
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexShrink: 0, marginBottom: "20px" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
                                                Lead trends: {kpiData.totalLeads?.toLocaleString() || '0'}
                                            </h3>
                                            <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 400 }}>
                                                Daily performance tracking (Total vs. Closed)
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFullscreenChart({
                                                title: `Lead trends: ${kpiData.totalLeads?.toLocaleString() || '0'}`,
                                                component: (
                                                    <div style={{ display: "flex", flexDirection: "column", padding: "16px", minHeight: "400px", gap: "16px" }}>
                                                        <div style={{ width: "100%", height: "320px" }}>
                                                            {(leadStatusTrends || []).length === 0 ? (
                                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: "13px" }}>
                                                                    No campaign data for lead trends
                                                                </div>
                                                            ) : (
                                                                <ResponsiveContainerC width="100%" height="100%">
                                                                    <ComposedChartC
                                                                        data={(leadStatusTrends || []).map((item: any) => ({
                                                                            campaign_name: (item.campaign_name || item.name || '').replace(/_/g, ' '),
                                                                            total_leads: Number(item.total_leads ?? item.total ?? 0),
                                                                            closed_leads: Number(item.closed_leads ?? item.closed ?? 0),
                                                                        }))}
                                                                        margin={{ top: 8, right: 52, bottom: (leadStatusTrends || []).length > 4 ? 64 : 56, left: (leadStatusTrends || []).length >= 6 ? 44 : 36 }}
                                                                    >
                                                                        <defs>
                                                                            <linearGradient id="overviewTotalFillFs" x1="0" y1="0" x2="0" y2="1">
                                                                                <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.25} />
                                                                                <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                                                                            </linearGradient>
                                                                        </defs>
                                                                        <CartesianGridC stroke="#f1f5f9" strokeDasharray="0" vertical={false} />
                                                                        <XAxisC dataKey="campaign_name" axisLine={false} tickLine={false} tick={<LeadTrendsXAxisTick dataLength={(leadStatusTrends || []).length} />} tickMargin={8} height={(leadStatusTrends || []).length > 4 ? 64 : 48} interval={0} />
                                                                        <YAxisC axisLine={false} tickLine={false} allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }} width={32} />
                                                                        <TooltipC content={({ active, payload, label }: any) => active && payload?.length ? (
                                                                            <div style={{ backgroundColor: "#fff", padding: "10px 14px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", fontSize: "12px" }}>
                                                                                <div style={{ fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>{(label ?? '').replace(/_/g, ' ')}</div>
                                                                                {payload.map((p: any) => <div key={p.dataKey} style={{ color: p.color, fontWeight: 500 }}>{p.name}: {p.value}</div>)}
                                                                            </div>
                                                                        ) : null} />
                                                                        <AreaC type="monotone" dataKey="total_leads" fill="url(#overviewTotalFillFs)" stroke="none" style={{ pointerEvents: "none" }} />
                                                                        <LineC type="monotone" dataKey="total_leads" name="Total" stroke="#4f46e5" strokeWidth={2} dot={{ r: 4, fill: "#4f46e5", stroke: "#fff", strokeWidth: 2, cursor: "pointer", onClick: () => { setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }} activeDot={{ r: 6, fill: "#4f46e5", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }} onClick={() => { setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); }} style={{ cursor: "pointer" }} />
                                                                        <LineC type="monotone" dataKey="closed_leads" name="Closed" stroke="#10b981" strokeWidth={2} dot={{ r: 4, fill: "#10b981", stroke: "#fff", strokeWidth: 2, cursor: "pointer", onClick: () => { setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }} activeDot={{ r: 6, fill: "#10b981", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }} onClick={() => { setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); }} style={{ cursor: "pointer" }} />
                                                                    </ComposedChartC>
                                                                </ResponsiveContainerC>
                                                            )}
                                                        </div>
                                                        {(leadStatusTrends || []).length > 0 && (
                                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                                                                <div role="button" tabIndex={0} onClick={() => { setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px", cursor: "pointer" }}>
                                                                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#4f46e5", flexShrink: 0 }} />
                                                                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>Total</span>
                                                                </div>
                                                                <div role="button" tabIndex={0} onClick={() => { setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px", cursor: "pointer" }}>
                                                                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#10b981", flexShrink: 0 }} />
                                                                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>Closed</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ),
                                                data: leadStatusTrends,
                                            })}
                                            title="View Fullscreen"
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                padding: "8px 14px",
                                                borderRadius: "20px",
                                                border: "none",
                                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                color: "#64748b",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                                                transform: "translateY(0)",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
                                                e.currentTarget.style.color = "#ffffff";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
                                                e.currentTarget.style.transform = "translateY(-1px)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                                e.currentTarget.style.color = "#64748b";
                                                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                                                e.currentTarget.style.transform = "translateY(0)";
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="15 3 21 3 21 9" />
                                                <polyline points="9 21 3 21 3 15" />
                                                <line x1="21" y1="3" x2="14" y2="10" />
                                                <line x1="3" y1="21" x2="10" y2="14" />
                                            </svg>
                                            <span>Expand</span>
                                        </button>
                                    </div>
                                    <div style={{ width: "100%", height: "280px" }}>
                                        {(leadStatusTrends || []).length === 0 ? (
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: "13px" }}>
                                                No campaign data for lead trends
                                            </div>
                                        ) : (
                                            <ResponsiveContainerC width="100%" height="100%">
                                                <ComposedChartC
                                                    data={(leadStatusTrends || []).map((item: any) => ({
                                                        campaign_name: (item.campaign_name || item.name || '').replace(/_/g, ' '),
                                                        total_leads: Number(item.total_leads ?? item.total ?? 0),
                                                        closed_leads: Number(item.closed_leads ?? item.closed ?? 0),
                                                    }))}
                                                    margin={{
                                                        top: 8,
                                                        right: 52,
                                                        bottom: (leadStatusTrends || []).length > 4 ? 64 : 56,
                                                        left: (leadStatusTrends || []).length >= 6 ? 44 : 36,
                                                    }}
                                                >
                                                    <defs>
                                                        <linearGradient id="overviewTotalFill" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.25} />
                                                            <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGridC stroke="#f1f5f9" strokeDasharray="0" vertical={false} />
                                                    <XAxisC
                                                        dataKey="campaign_name"
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tick={<LeadTrendsXAxisTick dataLength={(leadStatusTrends || []).length} />}
                                                        tickMargin={8}
                                                        height={(leadStatusTrends || []).length > 4 ? 64 : 48}
                                                        interval={0}
                                                    />
                                                    <YAxisC
                                                        axisLine={false}
                                                        tickLine={false}
                                                        allowDecimals={false}
                                                        tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }}
                                                        width={32}
                                                    />
                                                    <TooltipC
                                                        content={({ active, payload, label }: any) =>
                                                            active && payload?.length ? (
                                                                <div
                                                                    style={{
                                                                        backgroundColor: "#fff",
                                                                        padding: "10px 14px",
                                                                        borderRadius: "8px",
                                                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                                        border: "1px solid #e2e8f0",
                                                                        fontSize: "12px",
                                                                    }}
                                                                >
                                                                    <div style={{ fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>{(label ?? '').replace(/_/g, ' ')}</div>
                                                                    {payload.map((p: any) => (
                                                                        <div key={p.dataKey} style={{ color: p.color, fontWeight: 500 }}>
                                                                            {p.name}: {p.value}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : null
                                                        }
                                                    />
                                                    <AreaC
                                                        type="monotone"
                                                        dataKey="total_leads"
                                                        fill="url(#overviewTotalFill)"
                                                        stroke="none"
                                                        style={{ pointerEvents: "none" }}
                                                    />
                                                    <LineC
                                                        type="monotone"
                                                        dataKey="total_leads"
                                                        name="Total"
                                                        stroke="#4f46e5"
                                                        strokeWidth={2}
                                                        dot={{
                                                            r: 4,
                                                            fill: "#4f46e5",
                                                            stroke: "#fff",
                                                            strokeWidth: 2,
                                                            cursor: "pointer",
                                                            onClick: () => {
                                                                setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" });
                                                                setFunnelLeadsModalOpen(true);
                                                            },
                                                        }}
                                                        activeDot={{ r: 6, fill: "#4f46e5", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }}
                                                        onClick={() => {
                                                            setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" });
                                                            setFunnelLeadsModalOpen(true);
                                                        }}
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                    <LineC
                                                        type="monotone"
                                                        dataKey="closed_leads"
                                                        name="Closed"
                                                        stroke="#10b981"
                                                        strokeWidth={2}
                                                        dot={{
                                                            r: 4,
                                                            fill: "#10b981",
                                                            stroke: "#fff",
                                                            strokeWidth: 2,
                                                            cursor: "pointer",
                                                            onClick: () => {
                                                                setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" });
                                                                setFunnelLeadsModalOpen(true);
                                                            },
                                                        }}
                                                        activeDot={{ r: 6, fill: "#10b981", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }}
                                                        onClick={() => {
                                                            setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" });
                                                            setFunnelLeadsModalOpen(true);
                                                        }}
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                </ComposedChartC>
                                            </ResponsiveContainerC>
                                        )}
                                    </div>
                                    {(leadStatusTrends || []).length > 0 && (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "12px", marginTop: "8px", borderTop: "1px solid #e2e8f0" }}>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFunnelLeadsModalStage({ title: "Lead trends – Total", graph_name: "leads_trend", value: "all", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }}
                                                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px", cursor: "pointer" }}
                                            >
                                                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#4f46e5", flexShrink: 0 }} />
                                                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>Total</span>
                                            </div>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => {
                                                    setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFunnelLeadsModalStage({ title: "Lead trends – Closed", graph_name: "leads_trend", value: "closed", campaign_id: filters.campaign_id ?? "all" }); setFunnelLeadsModalOpen(true); } }}
                                                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px", cursor: "pointer" }}
                                            >
                                                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#10b981", flexShrink: 0 }} />
                                                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>Closed</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Right Column – Campaign Performance */}
                                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                                    {/* Campaign Performance Card */}
                                    <div
                                        style={{
                                            backgroundColor: "#ffffff",
                                            borderRadius: "18px",
                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                                            border: "1px solid #e2e8f0",
                                            padding: "24px",
                                            flex: "1 1 auto",
                                        }}
                                    >
                                        <h3 style={{ margin: "0 0 20px", fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
                                            Campaign performance: {top5CampaignPerformance.reduce((sum, campaign) => sum + campaign.value, 0)}
                                        </h3>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                                            {top5CampaignPerformance.length === 0 ? (
                                                <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8" }}>No campaign data yet</p>
                                            ) : (
                                                top5CampaignPerformance.map((item) => (
                                                    <div key={item.campaign_id ?? item.name} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <span style={{ fontSize: "14px", color: "#475569", fontWeight: 500 }}>{item.name}</span>
                                                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                                                                {item.value.toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div
                                                            style={{
                                                                height: "8px",
                                                                borderRadius: "4px",
                                                                backgroundColor: "#f1f5f9",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    height: "100%",
                                                                    width: `${Math.min(100, (item.value / maxCampaignValue) * 100)}%`,
                                                                    backgroundColor: item.color,
                                                                    borderRadius: "4px",
                                                                    transition: "width 0.3s ease",
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Exploding Pie (Pie of Pie) */}

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "24px",
                                    marginBottom: "24px",
                                    width: "100%",
                                    minWidth: 0,
                                }}
                            >
                                <ChartWidget
                                    title={`Leads per campaign: ${leadsPerCampaignDataWithIds.reduce((sum: number, campaign: any) => sum + (campaign.leads_count || 0), 0)}`}
                                    subtitle="Total leads generated"
                                    onExpand={() => setFullscreenChart({
                                        title: `Leads per campaign: ${leadsPerCampaignDataWithIds.reduce((sum: number, campaign: any) => sum + (campaign.leads_count || 0), 0)}`,
                                        component: (
                                            <LeadsPerCampaignChart
                                                data={leadsPerCampaignDataWithIds}
                                                onBarClick={(campaignId, campaignName) => {
                                                    setFunnelLeadsModalStage({ title: `Leads – ${campaignName}`, graph_name: "leads_per_campaign", value: String(campaignId), campaign_id: filters.campaign_id ?? "all" });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                            />
                                        ),
                                        data: leadsPerCampaignDataWithIds,
                                    })}
                                >
                                    <LeadsPerCampaignChart
                                        data={leadsPerCampaignDataWithIds}
                                        onBarClick={(campaignId, campaignName) => {
                                            setFunnelLeadsModalStage({ title: `Leads – ${campaignName}`, graph_name: "leads_per_campaign", value: String(campaignId), campaign_id: filters.campaign_id ?? "all" });
                                            setFunnelLeadsModalOpen(true);
                                        }}
                                    />
                                </ChartWidget>


                                <div
                                    style={{
                                        minWidth: 0,
                                        width: "100%",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "16px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                        border: "1px solid #e2e8f0",
                                        padding: "24px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "20px",
                                        minHeight: "340px",
                                    }}
                                >
                                    {/* Header: title, subtitle, expand */}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexShrink: 0 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>Lead source</h3>
                                            <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 400 }}>Leads by channel source</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFullscreenChart({
                                                title: "Lead channel",
                                                component: (
                                                    <div style={{ display: "flex", flexDirection: "column", padding: "16px", minHeight: "400px", gap: "16px" }}>
                                                        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: "320px" }}>
                                                            {(leadChannelNormalized || []).length === 0 ? (
                                                                <div style={{ color: "#64748b", fontSize: "14px" }}>No channel data</div>
                                                            ) : (
                                                                <>
                                                                    <ResponsiveContainerC width="100%" height={320}>
                                                                        <PieChartC>
                                                                            <PieC
                                                                                data={leadChannelNormalized}
                                                                                dataKey="value"
                                                                                nameKey="name"
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                innerRadius="58%"
                                                                                outerRadius="85%"
                                                                                paddingAngle={2}
                                                                                onClick={(data: any) => {
                                                                                    if (data?.channelValue != null) {
                                                                                        setFunnelLeadsModalStage({ title: `Leads – ${data.name}`, graph_name: "leads_channel", value: data.channelValue, campaign_id: filters.campaign_id ?? "all" });
                                                                                        setFunnelLeadsModalOpen(true);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {(leadChannelNormalized || []).map((entry: any, index: number) => (
                                                                                    <CellC key={`channel-fs-${index}`} fill={entry.color} style={{ cursor: entry.channelValue != null ? "pointer" : "default" }} />
                                                                                ))}
                                                                            </PieC>
                                                                            <TooltipC formatter={(value: number) => [value.toLocaleString(), "Leads"]} contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }} labelStyle={{ color: "#1e293b" }} />
                                                                        </PieChartC>
                                                                    </ResponsiveContainerC>
                                                                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none", zIndex: 2 }}>
                                                                        <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", marginBottom: "4px" }}>TOTAL LEADS</div>
                                                                        <div style={{ fontSize: "32px", fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>{leadChannelTotal.toLocaleString()}</div>

                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                        {(leadChannelNormalized || []).length > 0 && (
                                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                                                                {(leadChannelNormalized || []).map((row: any) => (
                                                                    <div
                                                                        key={row.name}
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "8px",
                                                                            padding: "6px 12px",
                                                                            borderRadius: "8px",
                                                                            cursor: row.channelValue != null ? "pointer" : "default",
                                                                        }}
                                                                        onClick={() => {
                                                                            if (row?.channelValue != null) {
                                                                                setFunnelLeadsModalStage({ title: `Leads – ${row.name}`, graph_name: "leads_channel", value: row.channelValue, campaign_id: filters.campaign_id ?? "all" });
                                                                                setFunnelLeadsModalOpen(true);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                                                                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{row.name}</span>

                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ),
                                                data: leadByChannelData,
                                            })}
                                            title="View Fullscreen"
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                padding: "8px 14px",
                                                borderRadius: "20px",
                                                border: "none",
                                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                color: "#64748b",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
                                                e.currentTarget.style.color = "#ffffff";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
                                                e.currentTarget.style.transform = "translateY(-1px)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                                e.currentTarget.style.color = "#64748b";
                                                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                                                e.currentTarget.style.transform = "translateY(0)";
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="15 3 21 3 21 9" />
                                                <polyline points="9 21 3 21 3 15" />
                                                <line x1="21" y1="3" x2="14" y2="10" />
                                                <line x1="3" y1="21" x2="10" y2="14" />
                                            </svg>
                                            <span>Expand</span>
                                        </button>
                                    </div>
                                    {/* Donut + center label */}
                                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: "16px" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: "260px" }}>
                                            {(leadChannelNormalized || []).length === 0 ? (
                                                <div style={{ color: "#94a3b8", fontSize: "14px" }}>No channel data</div>
                                            ) : (
                                                <ResponsiveContainerC width="100%" height={280}>
                                                    <PieChartC>
                                                        <PieC
                                                            data={leadChannelNormalized}
                                                            dataKey="value"
                                                            nameKey="name"
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius="58%"
                                                            outerRadius="85%"
                                                            paddingAngle={2}
                                                            onClick={(data: any) => {
                                                                if (data?.channelValue != null) {
                                                                    setFunnelLeadsModalStage({ title: `Leads – ${data.name}`, graph_name: "leads_channel", value: data.channelValue, campaign_id: filters.campaign_id ?? "all" });
                                                                    setFunnelLeadsModalOpen(true);
                                                                }
                                                            }}
                                                        >
                                                            {(leadChannelNormalized || []).map((entry: any, index: number) => (
                                                                <CellC key={`channel-${index}`} fill={entry.color} style={{ cursor: entry.channelValue != null ? "pointer" : "default" }} />
                                                            ))}
                                                        </PieC>
                                                        <TooltipC
                                                            formatter={(value: number) => [value.toLocaleString(), "Leads"]}
                                                            contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                                                            labelStyle={{ color: "#1e293b" }}
                                                        />
                                                    </PieChartC>
                                                </ResponsiveContainerC>
                                            )}
                                            {(leadChannelNormalized || []).length > 0 && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        textAlign: "center",
                                                        pointerEvents: "none",
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", marginBottom: "4px" }}>
                                                        TOTAL LEADS
                                                    </div>
                                                    <div style={{ fontSize: "28px", fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>
                                                        {leadChannelTotal.toLocaleString()}
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                        {/* Legend at bottom with count in parentheses (like Conversion engagement) */}
                                        {(leadChannelNormalized || []).length > 0 && (
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                                                {(leadChannelNormalized || []).map((row: any) => (
                                                    <div
                                                        key={row.name}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            padding: "6px 12px",
                                                            borderRadius: "8px",
                                                            cursor: row.channelValue != null ? "pointer" : "default",
                                                        }}
                                                        onClick={() => {
                                                            if (row?.channelValue != null) {
                                                                setFunnelLeadsModalStage({ title: `Leads – ${row.name}`, graph_name: "leads_channel", value: row.channelValue, campaign_id: filters.campaign_id ?? "all" });
                                                                setFunnelLeadsModalOpen(true);
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                                                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{row.name}</span>

                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            <ChartWidget
                                title={`Open leads per campaign: ${leadOpenTrends.reduce((sum, campaign) => sum + campaign.open_leads_count, 0)}`}
                                subtitle="Pending action items"
                                onExpand={() => setFullscreenChart({
                                    title: `Open leads per campaign: ${leadOpenTrends.reduce((sum, campaign) => sum + campaign.open_leads_count, 0)}`,
                                    component: (
                                        <OpenLeadsPerCampaignChart
                                            data={leadOpenTrends}
                                            onBarClick={(campaignId, campaignName) => {
                                                setFunnelLeadsModalStage({ title: `Leads – ${campaignName} (Open)`, graph_name: "open_leads_per_campaign", value: "active", status_type: "active", campaign_id: String(campaignId) });
                                                setFunnelLeadsModalOpen(true);
                                            }}
                                        />
                                    ),
                                    data: leadOpenTrends,
                                })}
                            >
                                <OpenLeadsPerCampaignChart
                                    data={leadOpenTrends}
                                    onBarClick={(campaignId, campaignName) => {
                                        setFunnelLeadsModalStage({ title: `Leads – ${campaignName} (Open)`, graph_name: "open_leads_per_campaign", value: "active", status_type: "active", campaign_id: String(campaignId) });
                                        setFunnelLeadsModalOpen(true);
                                    }}
                                />
                            </ChartWidget>

                        </>


                    )}

                    {activeTab === "leads" && (
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '16px' }}>
                                <div className="mw-200px" style={{ position: "relative", minWidth: '180px' }}>
                                    <div style={{ position: "absolute", top: "-9px", left: "5px", background: "white" }}>
                                        <p style={{ margin: "0", fontSize: "12px" }}>Campaign</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSectionCampaignId(e.target.value)}
                                        value={sectionCampaignId}
                                    >
                                        {campaigns.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "24px",
                                    marginBottom: "24px",
                                }}
                            >
                                <div style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "18px",
                                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                                    border: "1px solid #e2e8f0",
                                    padding: "24px",
                                    marginBottom: "0",
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", gap: "16px", flexWrap: "wrap" }}>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>
                                                Lead funnel: {funnelData.reduce((sum, stage) => sum + stage.lead_count, 0)}
                                            </h3>
                                            <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 400 }}>
                                                Conversion stages from lead to closed
                                            </p>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setFullscreenChart({
                                                        title: `Lead funnel: ${funnelData.reduce((sum, stage) => sum + stage.lead_count, 0)}`,
                                                        component: (
                                                            <div style={{ padding: "16px" }}>
                                                                {leadsLoading ? (
                                                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                                                                        <LoadingSpinner />
                                                                    </div>
                                                                ) : hasFunnelData ? (
                                                                    <ConversionFunnel
                                                                        data={funnelData}
                                                                        onStageClick={(stageName, statusType) => {
                                                                            setFunnelLeadsModalStage({
                                                                                title: `Leads – ${stageName}`,
                                                                                graph_name: "leads_funnel",
                                                                                value: stageName,
                                                                                status_type: statusType ?? "closed",
                                                                                campaign_id: sectionCampaignId ?? "",
                                                                            });
                                                                            setFunnelLeadsModalOpen(true);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div style={{ textAlign: "center", padding: "20px" }}>No data present</div>
                                                                )}
                                                            </div>
                                                        ),
                                                        data: funnelData,
                                                    })
                                                }
                                                title="View Fullscreen"
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    padding: "8px 14px",
                                                    borderRadius: "20px",
                                                    border: "none",
                                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                    color: "#64748b",
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                                                    transform: "translateY(0)",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
                                                    e.currentTarget.style.color = "#ffffff";
                                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
                                                    e.currentTarget.style.transform = "translateY(-1px)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                                    e.currentTarget.style.color = "#64748b";
                                                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                                                    e.currentTarget.style.transform = "translateY(0)";
                                                }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="15 3 21 3 21 9" />
                                                    <polyline points="9 21 3 21 3 15" />
                                                    <line x1="21" y1="3" x2="14" y2="10" />
                                                    <line x1="3" y1="21" x2="10" y2="14" />
                                                </svg>
                                                <span>Expand</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", justifyContent: "center", alignItems: "center", marginTop: "12px", marginBottom: "12px", width: "100%", boxSizing: "border-box" }}>
                                        {leadsLoading ? (
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                                                <LoadingSpinner />
                                            </div>
                                        ) : hasFunnelData ? (
                                            <ConversionFunnel
                                                data={funnelData}
                                                onStageClick={(stageName, statusType) => {
                                                    setFunnelLeadsModalStage({
                                                        title: `Leads – ${stageName}`,
                                                        graph_name: "leads_funnel",
                                                        value: stageName,
                                                        status_type: statusType ?? "closed",
                                                        campaign_id: sectionCampaignId ?? "",
                                                    });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                            />
                                        ) : (
                                            <div style={{ textAlign: "center", padding: "20px" }}>No data present</div>
                                        )}
                                    </div>
                                </div>

                                {(() => {
                                    const funnelData = funnelConversionRateData || [];
                                    const FUNNEL_COLORS = ["#5B3BD4", "#6B4EE0", "#5471F0", "#45A0F5", "#37CADF", "#22C9A8", "#10B981", "#059669", "#0D9488", "#14B8A6", "#2DD4BF", "#5EEAD4", "#99F6E4"];
                                    const n = funnelData.length;
                                    const step = n > 0 ? (50 - 8) / n : 0;
                                    const getTrapezoidClip = (i: number) =>
                                        `polygon(${i * step}% 0, ${100 - i * step}% 0, ${100 - (i + 1) * step}% 100%, ${(i + 1) * step}% 100%)`;
                                    const segmentCount = n;
                                    const useCompactForAll = n > 6;
                                    const funnelContainerHeight = 320;
                                    const renderFunnel = (segmentHeightFullscreen?: number) => {
                                        const inFullscreen = segmentHeightFullscreen != null;
                                        const containerH = inFullscreen ? n * segmentHeightFullscreen! : funnelContainerHeight;
                                        const segmentH = inFullscreen ? segmentHeightFullscreen! : (n > 0 ? funnelContainerHeight / n : 72);
                                        return (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    maxWidth: "720px",
                                                    margin: "0 auto",
                                                    height: containerH,
                                                    overflow: "hidden",
                                                    display: funnelData.length === 0 ? "block" : "flex",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                {funnelData.length === 0 ? (
                                                    <div style={{ textAlign: "center", padding: "48px 24px", color: "#94a3b8", fontSize: "15px" }}>No conversion data yet</div>
                                                ) : (
                                                    funnelData.map((stage: any, index: number) => {
                                                        const rate = Number(stage.percentage ?? stage.conversion_rate ?? 0);
                                                        const color = FUNNEL_COLORS[index % FUNNEL_COLORS.length];
                                                        const clipPath = getTrapezoidClip(index);
                                                        const stageValue = stage.funnel_stage ?? stage.stage_name ?? stage.default_stage;
                                                        const stageDisplayName = String(stageValue ?? "—").replace(/_/g, " ");
                                                        return (
                                                            <div
                                                                key={stage.funnel_stage ?? stage.stage_name ?? index}
                                                                role="button"
                                                                tabIndex={0}
                                                                style={{
                                                                    flex: inFullscreen ? "none" : 1,
                                                                    minHeight: 0,
                                                                    ...(inFullscreen ? { height: segmentH } : {}),
                                                                    width: "100%",
                                                                    clipPath,
                                                                    WebkitClipPath: clipPath,
                                                                    backgroundColor: color,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    padding: "4px 10px",
                                                                    boxSizing: "border-box",
                                                                    cursor: "pointer",
                                                                }}
                                                                onMouseEnter={(e) => setFunnelTooltip({ index, x: e.clientX, y: e.clientY })}
                                                                onMouseLeave={() => setFunnelTooltip(null)}
                                                                onClick={() => {
                                                                    if (stageValue) {
                                                                        setFunnelLeadsModalStage({ title: `Leads – ${stageDisplayName}`, graph_name: "leads_funnel", value: String(stageValue), status_type: stage.status_type ?? "closed", campaign_id: sectionCampaignId ?? "" });
                                                                        setFunnelLeadsModalOpen(true);
                                                                    }
                                                                }}
                                                            >
                                                                <span style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>{rate}%</span>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        );
                                    };
                                    return (
                                        <div
                                            style={{
                                                backgroundColor: "#ffffff",
                                                borderRadius: "18px",
                                                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                                                border: "1px solid #e2e8f0",
                                                padding: "24px",
                                                marginBottom: "0",
                                            }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px", gap: "16px", flexWrap: "wrap" }}>
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>Funnel conversion rate</h3>
                                                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 400 }}>Conversion rate at each stage from lead to close.</p>
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFullscreenChart({
                                                            title: `Funnel conversion rate: ${funnelData.reduce((sum: number, stage: any) => sum + stage.lead_count, 0)}`,
                                                            component: (
                                                                <div style={{ padding: "16px" }}>
                                                                    {renderFunnel(segmentCount > 10 ? 50 : 80)}
                                                                    {funnelData.length > 0 && (
                                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", justifyContent: "center", alignItems: "center", paddingTop: "16px", marginTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                                                                            {funnelData.map((stage: any, index: number) => {
                                                                                const stageValue = stage.funnel_stage ?? stage.stage_name ?? stage.default_stage;
                                                                                const stageName = String(stageValue ?? "—").replace(/_/g, " ");
                                                                                const color = FUNNEL_COLORS[index % FUNNEL_COLORS.length];
                                                                                return (
                                                                                    <div
                                                                                        key={stage.funnel_stage ?? stage.stage_name ?? index}
                                                                                        style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "8px", cursor: "pointer" }}
                                                                                        onMouseEnter={(e) => setFunnelTooltip({ index, x: e.clientX, y: e.clientY })}
                                                                                        onMouseLeave={() => setFunnelTooltip(null)}
                                                                                        onClick={() => {
                                                                                            if (stageValue) {
                                                                                                setFunnelLeadsModalStage({ title: `Leads – ${stageName}`, graph_name: "leads_funnel", value: String(stageValue), status_type: stage.status_type ?? "closed", campaign_id: sectionCampaignId ?? "" });
                                                                                                setFunnelLeadsModalOpen(true);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                                                                                        <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>{stageName}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ),
                                                            data: funnelConversionRateData,
                                                        })}
                                                        title="View Fullscreen"
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            padding: "8px 14px",
                                                            borderRadius: "20px",
                                                            border: "none",
                                                            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                            color: "#64748b",
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            cursor: "pointer",
                                                            transition: "all 0.2s ease",
                                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                                                            transform: "translateY(0)",
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
                                                            e.currentTarget.style.color = "#ffffff";
                                                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
                                                            e.currentTarget.style.transform = "translateY(-1px)";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                                            e.currentTarget.style.color = "#64748b";
                                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                                                            e.currentTarget.style.transform = "translateY(0)";
                                                        }}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="15 3 21 3 21 9" />
                                                            <polyline points="9 21 3 21 3 15" />
                                                            <line x1="21" y1="3" x2="14" y2="10" />
                                                            <line x1="3" y1="21" x2="10" y2="14" />
                                                        </svg>
                                                        <span>Expand</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    marginTop: "65px",
                                                    marginBottom: "60px",
                                                    width: "100%",
                                                    boxSizing: "border-box",
                                                }}
                                            >
                                                {renderFunnel()}
                                            </div>
                                            {funnelData.length > 0 && (
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", justifyContent: "center", alignItems: "center", paddingTop: "16px", marginTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                                                    {funnelData.map((stage: any, index: number) => {
                                                        const stageValue = stage.funnel_stage ?? stage.stage_name ?? stage.default_stage;
                                                        const stageName = String(stageValue ?? "—").replace(/_/g, " ");
                                                        const color = FUNNEL_COLORS[index % FUNNEL_COLORS.length];
                                                        return (
                                                            <div
                                                                key={stage.funnel_stage ?? stage.stage_name ?? index}
                                                                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "8px", cursor: "pointer", background: funnelTooltip?.index === index ? "rgba(0,0,0,0.06)" : "transparent" }}
                                                                onMouseEnter={(e) => setFunnelTooltip({ index, x: e.clientX, y: e.clientY })}
                                                                onMouseLeave={() => setFunnelTooltip(null)}
                                                                onClick={() => {
                                                                    if (stageValue) {
                                                                        setFunnelLeadsModalStage({ title: `Leads – ${stageName}`, graph_name: "leads_funnel", value: String(stageValue), status_type: stage.status_type ?? "closed", campaign_id: sectionCampaignId ?? "" });
                                                                        setFunnelLeadsModalOpen(true);
                                                                    }
                                                                }}
                                                            >
                                                                <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                                                                <span style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>{stageName}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {funnelTooltip !== null && funnelData[funnelTooltip.index] && (
                                                <div
                                                    style={{
                                                        position: "fixed",
                                                        left: funnelTooltip.x + 14,
                                                        top: funnelTooltip.y + 14,
                                                        zIndex: 9999,
                                                        backgroundColor: "#1e293b",
                                                        color: "#fff",
                                                        padding: "12px 16px",
                                                        borderRadius: "10px",
                                                        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                                                        border: "1px solid #334155",
                                                        fontSize: "13px",
                                                        minWidth: "180px",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    <div style={{ fontWeight: 700, marginBottom: "8px", color: "#f1f5f9" }}>Status</div>
                                                    <div style={{ marginBottom: "6px", color: "rgba(255,255,255,0.95)" }}>
                                                        {String(funnelData[funnelTooltip.index].funnel_stage ?? funnelData[funnelTooltip.index].stage_name ?? funnelData[funnelTooltip.index].default_stage ?? "—").replace(/_/g, " ")}
                                                    </div>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px", color: "rgba(255,255,255,0.9)" }}>
                                                        <span>Leads: <strong>{Number(funnelData[funnelTooltip.index].lead_count ?? funnelData[funnelTooltip.index].leads ?? 0).toLocaleString()}</strong></span>
                                                        <span>Conversion rate: <strong>{Number(funnelData[funnelTooltip.index].percentage ?? funnelData[funnelTooltip.index].conversion_rate ?? 0)}%</strong></span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}







                            </div>

                            <div
                                style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "16px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                    border: "1px solid #e2e8f0",
                                    padding: "24px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "20px",
                                    minHeight: "340px",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexShrink: 0 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1e293b" }}>Conversion engagement</h3>
                                        <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b", fontWeight: 400 }}>Track lead progression and measure how effectively leads convert to closed deals.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFullscreenChart({
                                            title: "Conversion engagement",
                                            component: (
                                                <ConversionEngagementFullscreen
                                                    conversionExplodingMainData={conversionExplodingMainData}
                                                    conversionFunnelData={conversionFunnelData ?? []}
                                                    conversionFunnelNormalized={conversionFunnelNormalized ?? []}
                                                    conversionFunnelTotal={conversionFunnelTotal}
                                                    campaignId={sectionCampaignId ?? ""}
                                                    setFunnelLeadsModalStage={setFunnelLeadsModalStage}
                                                    setFunnelLeadsModalOpen={setFunnelLeadsModalOpen}
                                                />
                                            ),
                                            data: conversionFunnelData,
                                        })}
                                        title="View Fullscreen"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            padding: "8px 14px",
                                            borderRadius: "20px",
                                            border: "none",
                                            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                            color: "#64748b",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                                            transform: "translateY(0)",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)";
                                            e.currentTarget.style.color = "#ffffff";
                                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.3)";
                                            e.currentTarget.style.transform = "translateY(-1px)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                                            e.currentTarget.style.color = "#64748b";
                                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
                                            e.currentTarget.style.transform = "translateY(0)";
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="15 3 21 3 21 9" />
                                            <polyline points="9 21 3 21 3 15" />
                                            <line x1="21" y1="3" x2="14" y2="10" />
                                            <line x1="3" y1="21" x2="10" y2="14" />
                                        </svg>
                                        <span>Expand</span>
                                    </button>
                                </div>


                                {leadsLoading ? (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: "260px" }}>
                                        <LoadingSpinner />
                                    </div>
                                ) : conversionExplodingMainData.length === 0 ? (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: "260px", color: "#94a3b8", fontSize: "14px" }}>
                                        No funnel data
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: "0" }}>
                                        <ExplodingPieChart
                                            mainData={conversionExplodingMainData}
                                            height={340}
                                            onSubSliceClick={({ category, status_type, value }) => {
                                                setFunnelLeadsModalStage({
                                                    title: `Leads – ${category}`,
                                                    graph_name: "leads_funnel",
                                                    value: value ?? category,
                                                    status_type: status_type ?? "closed",
                                                    campaign_id: sectionCampaignId ?? "",
                                                });
                                                setFunnelLeadsModalOpen(true);
                                            }}
                                        />
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", justifyContent: "center", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #e2e8f0", marginTop: "8px" }}>
                                            {(conversionFunnelMainPieData != null ? conversionFunnelMainPieData : conversionFunnelNormalized).map((row: any) => (
                                                <div key={row.name} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "8px" }}>
                                                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: row.color, flexShrink: 0 }} />
                                                    <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{row.displayLabel ?? row.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}


                    {activeTab === "tasks" && (
                        <div style={{ marginTop: '16px' }}>


                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '16px' }}>
                                <div className="mw-200px" style={{ position: "relative", minWidth: '180px' }}>
                                    <div style={{ position: "absolute", top: "-9px", left: "5px", background: "white" }}>
                                        <p style={{ margin: "0", fontSize: "12px" }}>Campaign</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSectionCampaignId(e.target.value)}
                                        value={sectionCampaignId}
                                    >
                                        {campaigns.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>



                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '16px'
                            }}>
                                {isCampaignTasksLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                                        <LoadingSpinner />
                                    </div>
                                ) : (
                                    <>
                                        <ChartWidget
                                            title={`Task status breakdown: ${completedTasksData.total_tasks}`}
                                            subtitle="Tasks by status and campaign"
                                            onExpand={() => setFullscreenChart({
                                                title: "Task status breakdown",
                                                component: <CompletedTasksOverTimeBarChart data={completedTasksData} dateRange={dateRange} onStatusClick={(name) => { setFunnelLeadsModalStage({ title: `Leads – ${name}`, graph_name: "leads_by_task", value: name, campaign_id: sectionCampaignId ?? "" }); setFunnelLeadsModalOpen(true); }} />,
                                                data: completedTasksData,
                                            })}
                                        >
                                            <CompletedTasksOverTimeBarChart
                                                data={completedTasksData}
                                                dateRange={dateRange}
                                                onStatusClick={(name) => { setFunnelLeadsModalStage({ title: `Leads – ${name}`, graph_name: "leads_by_task", value: name, campaign_id: sectionCampaignId ?? "" }); setFunnelLeadsModalOpen(true); }}
                                            />
                                        </ChartWidget>
                                    </>
                                )}


                            </div>
                        </div>
                    )}

                    {activeTab === "team-performance" && (
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '16px' }}>
                                <div className="mw-200px" style={{ position: "relative", minWidth: '180px' }}>
                                    <div style={{ position: "absolute", top: "-9px", left: "5px", background: "white" }}>
                                        <p style={{ margin: "0", fontSize: "12px" }}>Campaign</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSectionCampaignId(e.target.value)}
                                        value={sectionCampaignId}
                                    >
                                        {campaigns.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>



                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '16px'
                            }}>

                                {teamLoading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                                        <LoadingSpinner />
                                    </div>
                                ) : (
                                    <>
                                        <ChartWidget
                                            title={`Lead volume by assignee: ${teamPerformanceData.reduce((sum, member) => sum + member.assigned_leads, 0)}`}
                                            subtitle="Total vs closed leads per assignee"
                                            onExpand={() => setFullscreenChart({
                                                title: `Lead volume by assignee: ${teamPerformanceData.reduce((sum, member) => sum + member.assigned_leads, 0)}`,
                                                component: <AssigneePerformanceChart data={teamPerformanceData} onBarClick={(assigneeId, statusType, assigneeName) => { setFunnelLeadsModalStage({ title: `Leads – ${assigneeName} (${statusType === "all" ? "Total" : "Closed"})`, graph_name: "leads_volume_by_assignee", value: String(assigneeId), status_type: statusType, campaign_id: sectionCampaignId ?? "" }); setFunnelLeadsModalOpen(true); }} />,
                                                data: teamPerformanceData,
                                            })}
                                        >
                                            <AssigneePerformanceChart
                                                data={teamPerformanceData}
                                                onBarClick={(assigneeId, statusType, assigneeName) => {
                                                    setFunnelLeadsModalStage({ title: `Leads – ${assigneeName} (${statusType === "all" ? "Total" : "Closed"})`, graph_name: "leads_volume_by_assignee", value: String(assigneeId), status_type: statusType, campaign_id: sectionCampaignId ?? "" });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                            />
                                        </ChartWidget>
                                        <ChartWidget
                                            title={`Assignee closure rate`}
                                            subtitle="Percentage of leads converted per person"
                                            onExpand={() => setFullscreenChart({
                                                title: `Assignee closure rate`,
                                                component: <AssigneeConversionRateBarChart data={teamPerformanceData} onBarClick={(assigneeId, assigneeName) => { setFunnelLeadsModalStage({ title: `Leads – ${assigneeName} (Closed)`, graph_name: "leads_volume_by_assignee", value: String(assigneeId), status_type: "closed", campaign_id: sectionCampaignId ?? "" }); setFunnelLeadsModalOpen(true); }} />,
                                                data: teamPerformanceData,
                                            })}
                                        >
                                            <AssigneeConversionRateBarChart
                                                data={teamPerformanceData}
                                                onBarClick={(assigneeId, assigneeName) => {
                                                    setFunnelLeadsModalStage({ title: `Leads – ${assigneeName} (Closed)`, graph_name: "leads_volume_by_assignee", value: String(assigneeId), status_type: "closed", campaign_id: sectionCampaignId ?? "" });
                                                    setFunnelLeadsModalOpen(true);
                                                }}
                                            />
                                        </ChartWidget>
                                    </>
                                )}



                            </div>
                        </div>
                    )}

                    {activeTab === "whatsapp" && (
                        <div style={{ marginTop: "16px" }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '16px' }}>
                                <div className="mw-200px" style={{ position: "relative", minWidth: '180px' }}>
                                    <div style={{ position: "absolute", top: "-9px", left: "5px", background: "white" }}>
                                        <p style={{ margin: "0", fontSize: "12px" }}>Campaign</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setSectionCampaignId(e.target.value)}
                                        value={sectionCampaignId}
                                    >
                                        {campaigns.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mw-200px" style={{ position: "relative", minWidth: '180px' }}>
                                    <div style={{ position: "absolute", top: "-9px", left: "5px", background: "white" }}>
                                        <p style={{ margin: "0", fontSize: "12px" }}>Template</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        onChange={(e) => setWhatsappTemplateId(e.target.value)}
                                        value={whatsappTemplateId}
                                    >
                                        {getAllTemplates.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {isCampaignWhatsappVolumeLoading ? (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px" }}>
                                    {(() => {
                                        const chartData = whatsappChartData.length > 0 ? whatsappChartData : [];
                                        const chartHeight = 240;
                                        const fullscreenHeight = 380;
                                        const margin = { top: 8, right: 16, left: -8, bottom: 0 };
                                        const xAxisProps = { dataKey: "dateLabel" as const, tick: { fontSize: 11, fill: "#6b7280" }, tickLine: false, axisLine: { stroke: "#e5e7eb" } };
                                        const yAxisProps = { domain: [0, "auto" as const], allowDecimals: false, tick: { fontSize: 11, fill: "#6b7280" }, tickLine: false, axisLine: { stroke: "#e5e7eb" }, width: 28, tickFormatter: (v: number) => String(Math.round(v)) };
                                        const whatsappTeamId = typeof window !== "undefined" ? localStorage.getItem("teamId") : null;
                                        const formatWhatsappDate = (dateStr: string) => {
                                            if (!dateStr || dateStr.length < 10) return dateStr;
                                            const [y, m, d] = dateStr.slice(0, 10).split("-");
                                            return d && m && y ? `${d}-${m}-${y}` : dateStr;
                                        };
                                        const onWhatsappPointClick = (dateValue: string, graphName: string, chartTitle: string) => {
                                            if (!dateValue || !whatsappTeamId) return;
                                            setFunnelLeadsModalStage({ title: `WhatsApp – ${chartTitle} (${formatWhatsappDate(dateValue)})`, graph_name: graphName, value: dateValue, team_id: whatsappTeamId, template_id: whatsappTemplateId });
                                            setFunnelLeadsModalOpen(true);
                                        };
                                        const makeTooltipContent = (graphName: string, chartTitle: string) => ({ active, payload }: any) => {
                                            if (!active || !payload?.[0]) return null;
                                            const p = payload[0].payload;
                                            const dateVal = p?.date;
                                            if (dateVal) whatsappHoveredPayloadRef.current = { date: dateVal, graphName, chartTitle };
                                            const handleOpenModal = (e?: React.MouseEvent) => {
                                                e?.stopPropagation();
                                                e?.preventDefault();
                                                if (dateVal) onWhatsappPointClick(dateVal, graphName, chartTitle);
                                            };
                                            return (
                                                <div
                                                    style={{ backgroundColor: "#fff", padding: "10px 14px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", fontSize: "12px", minWidth: "140px", pointerEvents: "auto" }}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div style={{ fontWeight: 600, color: "#374151", marginBottom: "6px" }}>{p?.dateLabel}: {payload[0].value}</div>
                                                    <button
                                                        type="button"
                                                        onClick={handleOpenModal}
                                                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                                        style={{ width: "100%", padding: "6px 10px", fontSize: "11px", fontWeight: 600, color: "#6366f1", background: "rgba(99, 102, 241, 0.1)", border: "1px solid #6366f1", borderRadius: "6px", cursor: "pointer" }}
                                                    >
                                                        Click to view details
                                                    </button>
                                                </div>
                                            );
                                        };
                                        const handleChartAreaClick = () => {
                                            const r = whatsappHoveredPayloadRef.current;
                                            if (r) {
                                                onWhatsappPointClick(r.date, r.graphName, r.chartTitle);
                                                whatsappHoveredPayloadRef.current = null;
                                            }
                                        };
                                        const chartWrapperStyle = { width: "100%", height: "100%", cursor: "pointer" };
                                        const renderLeadsChart = (height: number, gradientId: string) => (
                                            <div style={{ ...chartWrapperStyle, height }} onClick={handleChartAreaClick}>
                                                <ResponsiveContainerC width="100%" height={height}>
                                                    <ComposedChartC data={chartData} margin={margin}>
                                                        <defs>
                                                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                                                                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.1} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGridC strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                                        <XAxisC {...xAxisProps} />
                                                        <YAxisC {...yAxisProps} />
                                                        <TooltipC content={makeTooltipContent("whatsapp_leads_generated", "Leads generated")} cursor={{ stroke: "#8b5cf6", strokeWidth: 1, strokeDasharray: "4 4" }} />
                                                        <AreaC type="monotone" dataKey="leads" stroke="#8b5cf6" fill={`url(#${gradientId})`} strokeWidth={2} />
                                                        <LineC type="monotone" dataKey="leads" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 6, fill: "#8b5cf6", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }} activeDot={{ r: 8, fill: "#8b5cf6", stroke: "#fff", strokeWidth: 2, cursor: "pointer", onClick: (_e: any, payload: any) => payload?.payload?.date && onWhatsappPointClick(payload.payload.date, "whatsapp_leads_generated", "Leads generated") }} onClick={(data: any) => data?.date && onWhatsappPointClick(data.date, "whatsapp_leads_generated", "Leads generated")} style={{ cursor: "pointer" }} />
                                                    </ComposedChartC>
                                                </ResponsiveContainerC>
                                            </div>
                                        );
                                        const renderCampaignChart = (height: number) => (
                                            <div style={{ ...chartWrapperStyle, height }} onClick={handleChartAreaClick}>
                                                <ResponsiveContainerC width="100%" height={height}>
                                                    <BarChartC data={chartData} margin={margin}>
                                                        <CartesianGridC strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                                        <XAxisC {...xAxisProps} />
                                                        <YAxisC {...yAxisProps} />
                                                        <TooltipC content={makeTooltipContent("whatsapp_campaign_insight", "Campaign insights")} cursor={{ fill: "rgba(16, 185, 129, 0.1)" }} />
                                                        <BarC dataKey="campaign" fill="#10b981" radius={[4, 4, 0, 0]} onClick={(data: any) => data?.date && onWhatsappPointClick(data.date, "whatsapp_campaign_insight", "Campaign insights")} cursor="pointer" />
                                                    </BarChartC>
                                                </ResponsiveContainerC>
                                            </div>
                                        );
                                        const renderDeliveredChart = (height: number, gradientId: string) => (
                                            <div style={{ ...chartWrapperStyle, height }} onClick={handleChartAreaClick}>
                                                <ResponsiveContainerC width="100%" height={height}>
                                                    <ComposedChartC data={chartData} margin={margin}>
                                                        <defs>
                                                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                                <stop offset="95%" stopColor="#34d399" stopOpacity={0.1} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGridC strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                                        <XAxisC {...xAxisProps} />
                                                        <YAxisC {...yAxisProps} />
                                                        <TooltipC content={makeTooltipContent("whatsapp_delivered_messages", "Delivered messages")} cursor={{ stroke: "#10b981", strokeWidth: 1, strokeDasharray: "4 4" }} />
                                                        <AreaC type="monotone" dataKey="delivered" stroke="#10b981" fill={`url(#${gradientId})`} strokeWidth={2} />
                                                        <LineC type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} dot={{ r: 6, fill: "#10b981", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }} activeDot={{ r: 8, fill: "#10b981", stroke: "#fff", strokeWidth: 2, cursor: "pointer", onClick: (_e: any, payload: any) => payload?.payload?.date && onWhatsappPointClick(payload.payload.date, "whatsapp_delivered_messages", "Delivered messages") }} onClick={(data: any) => data?.date && onWhatsappPointClick(data.date, "whatsapp_delivered_messages", "Delivered messages")} style={{ cursor: "pointer" }} />
                                                    </ComposedChartC>
                                                </ResponsiveContainerC>
                                            </div>
                                        );
                                        const renderReadChart = (height: number) => (
                                            <div style={{ ...chartWrapperStyle, height }} onClick={handleChartAreaClick}>
                                                <ResponsiveContainerC width="100%" height={height}>
                                                    <ComposedChartC data={chartData} margin={margin}>
                                                        <CartesianGridC strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                                        <XAxisC {...xAxisProps} />
                                                        <YAxisC {...yAxisProps} />
                                                        <TooltipC content={makeTooltipContent("whatsapp_read_messages", "Read messages")} cursor={{ stroke: "#6366f1", strokeWidth: 1, strokeDasharray: "4 4" }} />
                                                        <LineC type="monotone" dataKey="read" stroke="#6366f1" strokeWidth={2} dot={{ r: 6, fill: "#6366f1", stroke: "#fff", strokeWidth: 2, cursor: "pointer" }} activeDot={{ r: 8, fill: "#6366f1", stroke: "#fff", strokeWidth: 2, cursor: "pointer", onClick: (_e: any, payload: any) => payload?.payload?.date && onWhatsappPointClick(payload.payload.date, "whatsapp_read_messages", "Read messages") }} onClick={(data: any) => data?.date && onWhatsappPointClick(data.date, "whatsapp_read_messages", "Read messages")} style={{ cursor: "pointer" }} />
                                                    </ComposedChartC>
                                                </ResponsiveContainerC>
                                            </div>
                                        );
                                        return (
                                            <>
                                                <ChartWidget
                                                    title="Leads generated"
                                                    subtitle={campaignWhatsappVolumeData?.whatsapp_leads_generated?.total_whatsapp_responded_leads != null ? `Total: ${campaignWhatsappVolumeData.whatsapp_leads_generated.total_whatsapp_responded_leads}` : undefined}
                                                    onExpand={() => setFullscreenChart({ title: "WhatsApp – Leads generated", component: renderLeadsChart(fullscreenHeight, "whatsappLeadsFillFs"), data: chartData })}
                                                >
                                                    {chartData.length === 0 ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: chartHeight, color: "#94a3b8", fontSize: "13px" }}>No data</div> : renderLeadsChart(chartHeight, "whatsappLeadsFill")}
                                                </ChartWidget>
                                                <ChartWidget
                                                    title="Campaign insights"
                                                    subtitle={campaignWhatsappVolumeData?.whatsapp_campaign_insight?.total_whatsapp_leads != null ? `Total: ${campaignWhatsappVolumeData.whatsapp_campaign_insight.total_whatsapp_leads}` : undefined}
                                                    onExpand={() => setFullscreenChart({ title: "WhatsApp – Campaign insights", component: renderCampaignChart(fullscreenHeight), data: chartData })}
                                                >
                                                    {chartData.length === 0 ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: chartHeight, color: "#94a3b8", fontSize: "13px" }}>No data</div> : renderCampaignChart(chartHeight)}
                                                </ChartWidget>
                                                <ChartWidget
                                                    title="Delivered messages"
                                                    subtitle={campaignWhatsappVolumeData?.whatsapp_delivered_messages?.total_whatsapp_delivered_leads != null ? `Total: ${campaignWhatsappVolumeData.whatsapp_delivered_messages.total_whatsapp_delivered_leads}` : undefined}
                                                    onExpand={() => setFullscreenChart({ title: "WhatsApp – Delivered messages", component: renderDeliveredChart(fullscreenHeight, "whatsappDeliveredFillFs"), data: chartData })}
                                                >
                                                    {chartData.length === 0 ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: chartHeight, color: "#94a3b8", fontSize: "13px" }}>No data</div> : renderDeliveredChart(chartHeight, "whatsappDeliveredFill")}
                                                </ChartWidget>
                                                <ChartWidget
                                                    title="Read messages"
                                                    subtitle={campaignWhatsappVolumeData?.whatsapp_read_messages?.total_whatsapp_read_leads != null ? `Total: ${campaignWhatsappVolumeData.whatsapp_read_messages.total_whatsapp_read_leads}` : undefined}
                                                    onExpand={() => setFullscreenChart({ title: "WhatsApp – Read messages", component: renderReadChart(fullscreenHeight), data: chartData })}
                                                >
                                                    {chartData.length === 0 ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: chartHeight, color: "#94a3b8", fontSize: "13px" }}>No data</div> : renderReadChart(chartHeight)}
                                                </ChartWidget>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    )}
                     </div>
                </>
            )}



            <TableModal
                isOpen={funnelLeadsModalOpen}
                onRequestClose={() => { setFunnelLeadsModalOpen(false); setFunnelLeadsModalStage(null); setClickToExpandPage(1); }}
                title={funnelLeadsModalStage?.graph_name === "leads_by_task" ? "Lead task" : funnelLeadsModalStage?.title ?? "Leads"}
                contentLabel="Leads by stage"
                emptyMessage="No leads in this segment."
                data={clickToExpandRows}
                isLoading={isClickToExpandLoading}
                getRowKey={(row: any, idx) => row?.lead_id ?? row?.id ?? idx}
                onRowClick={(row: any) => {
                    const leadId = row?.lead_id ?? row?.id;
                    if (!leadId) return;
                    if (funnelLeadsModalStage?.graph_name === "leads_by_task") {
                        navigate(`/campaign/lead/${leadId}`, {
                            state: { initialPane: "TasksTab" as const },
                        });
                    } else {
                        navigate(`/campaign/lead/${leadId}`);
                    }
                }}
                totalCount={clickToExpandPagination?.totalCount}
                currentPage={clickToExpandPagination?.currentPage}
                totalPages={clickToExpandPagination?.totalPages}
                pageSize={clickToExpandPagination?.pageSize}
                onPageChange={setClickToExpandPage}
                columns={
                    funnelLeadsModalStage?.graph_name === "leads_by_task"
                        ? [
                            {
                                label: "Lead ID",
                                render: (lead: any) => lead?.lead_id ?? lead?.id ?? "—",
                            },
                            {
                                label: "Title",
                                render: (lead: any) => lead?.title ?? "—",
                            },
                            {
                                label: "Status",
                                render: (lead: any) => {
                                    const status = lead?.status ?? "—";
                                    const STATUS_COLOR: Record<string, { bg: string; color: string }> = {
                                        active: { bg: "#ede9fe", color: "#5b21b6" },
                                        done: { bg: "#d1fae5", color: "#065f46" },
                                        cancelled: { bg: "#fee2e2", color: "#991b1b" },
                                        rescheduled: { bg: "#fef3c7", color: "#92400e" },
                                        "Due Today": { bg: "#e0e7ff", color: "#4338ca" },
                                        Upcoming: { bg: "#ede9fe", color: "#5b21b6" },
                                        Overdue: { bg: "#fee2e2", color: "#991b1b" },
                                    };
                                    const cfg = STATUS_COLOR[status] ?? { bg: "#f1f5f9", color: "#475569" };
                                    return (
                                        <span style={{
                                            display: "inline-block",
                                            padding: "2px 10px",
                                            borderRadius: "999px",
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            background: cfg.bg,
                                            color: cfg.color,
                                        }}>
                                            {status}
                                        </span>
                                    );
                                },
                            },


                        ]
                        : [
                            {
                                label: "Lead ID",
                                render: (lead: any) => lead?.lead_id ?? lead?.id ?? "—",
                            },
                            {
                                label: "Name",
                                render: (lead: any) => lead?.name ?? "N/A",
                            },
                            {
                                label: "Phone number",
                                render: (lead: any) => lead?.phone_number ?? lead?.phone ?? "—",
                            },
                            {
                                label: "Created at",
                                render: (lead: any) => {
                                    const raw = lead?.created_at ?? lead?.created;
                                    if (!raw) return "—";
                                    const d = new Date(raw);
                                    if (Number.isNaN(d.getTime())) return "—";
                                    const dd = String(d.getDate()).padStart(2, "0");
                                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                                    const yyyy = d.getFullYear();
                                    const hh = String(d.getHours()).padStart(2, "0");
                                    const min = String(d.getMinutes()).padStart(2, "0");
                                    const ss = String(d.getSeconds()).padStart(2, "0");
                                    return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
                                },
                            },
                        ]
                }
            />


            <FullscreenModal
                key={fullscreenChart?.title}
                isOpen={fullscreenChart !== null}
                onClose={() => setFullscreenChart(null)}
                title={fullscreenChart?.title || ''}
                data={fullscreenChart?.data}
            >
                {fullscreenChart?.component}
            </FullscreenModal>

            {/* Dashboard Content */}
            {/* <div style={{ flex: 1 }}>{renderTabContent()}</div> */}
        </div>
    )
}

export default LeadDashboard

// 